import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { combineLatest, firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { AuthService } from '../../core/auth/auth.service';
import { UserContextService } from '../../core/auth/user-context.service';
import {
  CollectionPage,
  InMemoryDataService,
  InstructorRateHistoryEntry,
  InstructorSettlementMonth
} from '../../core/data/in-memory-data.service';
import { CourseInstance } from '../../core/models/domain.models';
import { EntityConfig, getEntityConfig } from '../../core/config/entity-config';
import { resolveDefaultRateForCategoryName } from '../../core/config/instructor-rates.config';
import {
  normalizeResourceSubType,
  normalizeResourceType,
  RESOURCE_SUB_TYPE_OPTIONS,
  RESOURCE_TYPE_OPTIONS
} from '../../core/config/resource-enums.config';
import { formatPln } from '../../core/utils/currency.util';
import { dateKey, formatDisplayDate, formatDisplayTime } from '../../core/utils/date-format.util';
import { LocalizationService } from '../../core/i18n/localization.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { AddLessonDialogComponent } from '../resource-list/add-lesson-dialog.component';
import { EditResourceDialogComponent } from '../resource-list/edit-resource-dialog.component';
import { AddPaymentDialogComponent } from './add-payment-dialog.component';
import { AssignRelationDialogComponent } from './assign-relation-dialog.component';
import { CancelLessonDialogComponent } from './cancel-lesson-dialog.component';
import {
  EditInstructorRatesDialogComponent,
  EditInstructorRatesDialogResult
} from './edit-instructor-rates-dialog.component';
import { CreateResourceDialogComponent } from '../resource-list/create-resource-dialog.component';
import {
  InstructorLessonsFilterDialogComponent,
  InstructorLessonsFilterDialogResult
} from './instructor-lessons-filter-dialog.component';
import {
  MoveStudentCourseDialogComponent,
  MoveStudentCourseOption
} from './move-student-course-dialog.component';
import { InstructorAvailabilityDialogComponent } from './instructor-availability-dialog.component';
import { InstructorUnavailabilityDialogComponent } from './instructor-unavailability-dialog.component';
import { InstructorCalendarDialogComponent } from './instructor-calendar.component';
import { UnlinkRelationDialogComponent } from './unlink-relation-dialog.component';
import { ConfirmActionDialogComponent } from './confirm-action-dialog.component';

type DetailEntityRow = Record<string, unknown> & { id: string | number; status: string };
const EMPTY_DETAIL_PAGE: CollectionPage<DetailEntityRow> = {
  page: 1,
  pageSize: 100,
  total: 0,
  items: []
};
const EMPTY_RATE_HISTORY_PAGE: CollectionPage<InstructorRateHistoryEntry> = {
  page: 1,
  pageSize: 20,
  total: 0,
  items: []
};

interface StatusAction {
  nextStatus: string;
  labelKey: string;
  icon: string;
}

interface OverviewSection {
  key: 'basic' | 'contact' | 'address';
  label: string;
  fields: Array<{ key: string; label: string; value: string }>;
}

interface InstructorResourceAssignmentOption {
  id: string | number;
  label: string;
  resourceId: string | number;
}

type UnlinkRelationKind =
  | 'studentInstructor'
  | 'studentCourse'
  | 'instructorStudent'
  | 'instructorCourse'
  | 'instructorResource'
  | 'resourceCourse';

@Component({
  selector: 'app-resource-details-page',
  standalone: true,
  imports: [
    RouterLink,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatTableModule,
    MatProgressBarModule,
    TranslatePipe
  ],
  templateUrl: './resource-details-page.component.html',
  styleUrl: './resource-details-page.component.scss'
})
export class ResourceDetailsPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dataService = inject(InMemoryDataService);
  private readonly userContext = inject(UserContextService);
  private readonly auth = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly localization = inject(LocalizationService);
  private readonly paramMap = toSignal(this.route.paramMap, { initialValue: this.route.snapshot.paramMap });
  readonly instructorLessonDateFrom = signal('');
  readonly instructorLessonDateTo = signal('');
  readonly instructorLessonStudentId = signal<string | number | null>(null);
  readonly instructorLessonsQuickFilter = signal<'all' | 'completed' | 'planned'>('all');
  readonly instructorLessonsPageIndex = signal(0);
  readonly instructorLessonsPageSize = signal(10);
  readonly instructorStudentsPageIndex = signal(0);
  readonly instructorStudentsPageSize = signal(10);
  readonly instructorStudentsQuery = signal('');
  readonly instructorStudentsStatus = signal<'all' | 'active' | 'disabled'>('all');
  readonly detailsTabIndex = signal(0);
  readonly expandedSettlementMonth = signal<string | null>(null);
  readonly instructorSettlementsRefresh = signal(0);
  readonly settlingMonth = signal<string | null>(null);
  readonly detailsRefresh = signal(0);
  readonly studentCoursesReload = signal(0);
  readonly studentPaymentsReload = signal(0);
  readonly embeddedLessonsPageIndex = signal(0);
  readonly embeddedLessonsPageSize = signal(10);
  readonly embeddedLessonsDateFrom = signal('');
  readonly embeddedLessonsDateTo = signal('');
  readonly embeddedPaymentsPageIndex = signal(0);
  readonly embeddedPaymentsPageSize = signal(10);
  readonly embeddedPaymentsDateFrom = signal('');
  readonly embeddedPaymentsDateTo = signal('');

  readonly entity = computed<EntityConfig | null>(() => {
    const key = this.paramMap().get('entityKey');
    return getEntityConfig(key) ?? null;
  });

  readonly id = computed(() => this.paramMap().get('id'));
  readonly row = toSignal(
    combineLatest([toObservable(this.entity), toObservable(this.id), toObservable(this.detailsRefresh)]).pipe(
      switchMap(([config, id]) => {
        if (!config || !id) {
          return of(null);
        }
        return this.dataService.fetchEntity$(config.key, id).pipe(
          map((entity) => entity as unknown as DetailEntityRow),
          switchMap((entity) => config.key === 'payments'
            ? this.loadPaymentReferences$([entity]).pipe(map(() => entity))
            : of(entity)),
          catchError(() => of(null))
        );
      })
    ),
    { initialValue: null as DetailEntityRow | null }
  );

  private readonly studentLessonsPage = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.detailsTabIndex), toObservable(this.embeddedLessonsPageIndex), toObservable(this.embeddedLessonsPageSize), toObservable(this.embeddedLessonsDateFrom), toObservable(this.embeddedLessonsDateTo)]).pipe(
      switchMap(([row, tabIndex, pageIndex, pageSize, dateFrom, dateTo]) => {
        if (this.detailTabKey(tabIndex) !== 'studentLessons' || !row) {
          return of(EMPTY_DETAIL_PAGE);
        }
        return this.dataService
          .fetchCollectionPage$('lessons', {
            page: pageIndex + 1,
            pageSize,
            filters: { studentId: row.id, dateFrom, dateTo, sort: 'executionDate', order: 'desc' }
          })
          .pipe(
            map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
            switchMap((page) => this.loadLessonReferences$(page.items).pipe(map(() => page))),
            catchError(() => of(EMPTY_DETAIL_PAGE))
          );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly studentPaymentsPage = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.detailsTabIndex), toObservable(this.studentPaymentsReload), toObservable(this.embeddedPaymentsPageIndex), toObservable(this.embeddedPaymentsPageSize), toObservable(this.embeddedPaymentsDateFrom), toObservable(this.embeddedPaymentsDateTo)]).pipe(
      switchMap(([row, tabIndex, , pageIndex, pageSize, dateFrom, dateTo]) => {
        if (this.detailTabKey(tabIndex) !== 'studentPayments' || !row) {
          return of(EMPTY_DETAIL_PAGE);
        }
        return this.dataService
          .fetchCollectionPage$('payments', {
            page: pageIndex + 1,
            pageSize,
            filters: { studentId: row.id, dateFrom, dateTo, sort: 'paymentDate', order: 'desc' }
          })
          .pipe(
            map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
            switchMap((page) => this.loadPaymentReferences$(page.items).pipe(map(() => page))),
            catchError(() => of(EMPTY_DETAIL_PAGE))
          );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly studentCoursesPage = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.detailsTabIndex), toObservable(this.studentCoursesReload)]).pipe(
      switchMap(([row, tabIndex]) => {
        if (this.detailTabKey(tabIndex) !== 'studentCourses' || !row) {
          return of(EMPTY_DETAIL_PAGE);
        }
        return this.dataService
          .fetchCollectionPageFromPath$('courseInstances', `students/${row.id}/course-instances`, {
            page: 1,
            pageSize: 100,
            filters: { sort: 'dateStarted', order: 'desc' }
          })
          .pipe(
            map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
            catchError(() => of(EMPTY_DETAIL_PAGE))
          );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly instructorLessonsPage = toSignal(
    combineLatest([
      toObservable(this.row),
      toObservable(this.detailsTabIndex),
      toObservable(this.instructorLessonsPageIndex),
      toObservable(this.instructorLessonsPageSize),
      toObservable(this.instructorLessonDateFrom),
      toObservable(this.instructorLessonDateTo),
      toObservable(this.instructorLessonStudentId),
      toObservable(this.instructorLessonsQuickFilter)
    ]).pipe(
      switchMap(([row, tabIndex, pageIndex, pageSize, dateFrom, dateTo, studentId, quickFilter]) => {
        if (this.detailTabKey(tabIndex) !== 'instructorLessons' || !row) {
          return of({ ...EMPTY_DETAIL_PAGE, pageSize });
        }
        return this.dataService
          .fetchCollectionPage$('lessons', {
            page: pageIndex + 1,
            pageSize,
            filters: {
              instructorId: row.id,
              dateFrom,
              dateTo,
              studentId,
              status: quickFilter === 'all' ? '' : quickFilter,
              sort: 'executionDate',
              order: 'desc'
            }
          })
          .pipe(
            map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
            switchMap((page) => this.loadLessonReferences$(page.items).pipe(map(() => page))),
            catchError(() => of({ ...EMPTY_DETAIL_PAGE, page: pageIndex + 1, pageSize }))
          );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly studentInstructorsPage = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.detailsTabIndex), toObservable(this.embeddedLessonsPageIndex), toObservable(this.embeddedLessonsPageSize), toObservable(this.embeddedLessonsDateFrom), toObservable(this.embeddedLessonsDateTo)]).pipe(
      switchMap(([row, tabIndex, pageIndex, pageSize, dateFrom, dateTo]) => {
        if (this.detailTabKey(tabIndex) !== 'studentInstructors' || !row) {
          return of(EMPTY_DETAIL_PAGE);
        }
        return this.dataService.fetchCollectionPage$('instructors', {
          page: pageIndex + 1,
          pageSize,
          filters: { studentId: row.id, sort: 'surname', order: 'asc' }
        }).pipe(
          map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
          catchError(() => of(EMPTY_DETAIL_PAGE))
        );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly instructorStudentsPage = toSignal(
    combineLatest([
      toObservable(this.row),
      toObservable(this.detailsTabIndex),
      toObservable(this.instructorStudentsPageIndex),
      toObservable(this.instructorStudentsPageSize),
      toObservable(this.instructorStudentsQuery).pipe(debounceTime(250), distinctUntilChanged()),
      toObservable(this.instructorStudentsStatus)
    ]).pipe(
      switchMap(([row, tabIndex, pageIndex, pageSize, query, status]) => {
        if (this.detailTabKey(tabIndex) !== 'instructorStudents' || !row) {
          return of({ ...EMPTY_DETAIL_PAGE, pageSize });
        }
        return this.dataService.fetchCollectionPage$('students', {
          page: pageIndex + 1,
          pageSize,
          filters: { instructorId: row.id, query: query.trim(), status: status === 'all' ? '' : status, sort: 'surname', order: 'asc' }
        }).pipe(
          map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
          catchError(() => of({ ...EMPTY_DETAIL_PAGE, page: pageIndex + 1, pageSize }))
        );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly instructorCoursesPage = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.detailsTabIndex)]).pipe(
      switchMap(([row, tabIndex]) => {
        if (this.detailTabKey(tabIndex) !== 'instructorCourses' || !row) {
          return of(EMPTY_DETAIL_PAGE);
        }
        return this.dataService.fetchCollectionPage$('courseInstances', {
          page: 1,
          pageSize: 100,
          filters: { instructorId: row.id, sort: 'dateStarted', order: 'desc' }
        }).pipe(
          map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
          catchError(() => of(EMPTY_DETAIL_PAGE))
        );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly instructorResourcesPage = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.detailsTabIndex)]).pipe(
      switchMap(([row, tabIndex]) => {
        if (this.detailTabKey(tabIndex) !== 'instructorResources' || !row) {
          return of(EMPTY_DETAIL_PAGE);
        }
        return this.dataService.fetchCollectionPage$('resources', {
          page: 1,
          pageSize: 100,
          filters: { instructorId: row.id, sort: 'name', order: 'asc' }
        }).pipe(
          map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
          catchError(() => of(EMPTY_DETAIL_PAGE))
        );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly resourceCoursesPage = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.detailsTabIndex)]).pipe(
      switchMap(([row, tabIndex]) => {
        if (this.detailTabKey(tabIndex) !== 'resourceCourses' || !row) {
          return of(EMPTY_DETAIL_PAGE);
        }
        return this.dataService.fetchCollectionPage$('courseInstances', {
          page: 1,
          pageSize: 100,
          filters: { resourceId: row.id, sort: 'dateStarted', order: 'desc' }
        }).pipe(
          map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
          catchError(() => of(EMPTY_DETAIL_PAGE))
        );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly relatedLessonsPage = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.detailsTabIndex), toObservable(this.embeddedLessonsPageIndex), toObservable(this.embeddedLessonsPageSize), toObservable(this.embeddedLessonsDateFrom), toObservable(this.embeddedLessonsDateTo)]).pipe(
      switchMap(([row, tabIndex, pageIndex, pageSize, dateFrom, dateTo]) => {
        const key = this.entityKey();
        if (this.detailTabKey(tabIndex) !== 'relatedLessons' || !row || (key !== 'resources' && key !== 'courseInstances')) {
          return of(EMPTY_DETAIL_PAGE);
        }
        return this.dataService.fetchCollectionPage$('lessons', {
          page: pageIndex + 1,
          pageSize,
          filters: {
            resourceId: key === 'resources' ? row.id : '',
            courseInstanceId: key === 'courseInstances' ? row.id : '',
            dateFrom,
            dateTo,
            sort: 'executionDate',
            order: 'desc'
          }
        }).pipe(
          map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
          switchMap((page) => this.loadLessonReferences$(page.items).pipe(map(() => page))),
          catchError(() => of(EMPTY_DETAIL_PAGE))
        );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly relatedPaymentsPage = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.detailsTabIndex), toObservable(this.embeddedPaymentsPageIndex), toObservable(this.embeddedPaymentsPageSize), toObservable(this.embeddedPaymentsDateFrom), toObservable(this.embeddedPaymentsDateTo)]).pipe(
      switchMap(([row, tabIndex, pageIndex, pageSize, dateFrom, dateTo]) => {
        if (this.detailTabKey(tabIndex) !== 'relatedPayments' || !row) {
          return of(EMPTY_DETAIL_PAGE);
        }
        return this.dataService.fetchCollectionPage$('payments', {
          page: pageIndex + 1,
          pageSize,
          filters: { courseInstanceId: row.id, dateFrom, dateTo, sort: 'paymentDate', order: 'desc' }
        }).pipe(
          map((page) => ({ ...page, items: page.items as unknown as DetailEntityRow[] })),
          switchMap((page) => this.loadPaymentReferences$(page.items).pipe(map(() => page))),
          catchError(() => of(EMPTY_DETAIL_PAGE))
        );
      })
    ),
    { initialValue: EMPTY_DETAIL_PAGE }
  );

  private readonly instructorSettlements = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.instructorSettlementsRefresh)]).pipe(
      switchMap(([row]) => {
        if (this.entityKey() !== 'instructors' || !row) {
          return of([] as InstructorSettlementMonth[]);
        }
        return this.dataService.fetchInstructorSettlements$(row.id, { includePlanned: true }).pipe(
          map((response) => response.items ?? []),
          catchError(() => of([] as InstructorSettlementMonth[]))
        );
      })
    ),
    { initialValue: [] as InstructorSettlementMonth[] }
  );

  private readonly instructorRateHistoryPage = toSignal(
    combineLatest([toObservable(this.row), toObservable(this.detailsTabIndex)]).pipe(
      switchMap(([row, tabIndex]) => {
        if (this.detailTabKey(tabIndex) !== 'instructorRateHistory' || !row) {
          return of(EMPTY_RATE_HISTORY_PAGE);
        }
        return this.dataService.fetchInstructorRateHistory$(row.id, { page: 1, pageSize: 20 }).pipe(
          catchError(() => of(EMPTY_RATE_HISTORY_PAGE))
        );
      })
    ),
    { initialValue: EMPTY_RATE_HISTORY_PAGE }
  );

  readonly entityKey = computed(() => this.entity()?.key ?? null);
  readonly entityTitle = computed(() => {
    const config = this.entity();
    if (!config) {
      return '';
    }
    const translated = this.localization.translate(`entity.${config.key}`);
    return translated === `entity.${config.key}` ? config.title : translated;
  });

  private loadLessonReferences$(lessons: DetailEntityRow[]): Observable<void> {
    if (lessons.length === 0) {
      return of(undefined);
    }
    const loaders = [];
    const lessonsWithoutStudentName = lessons.filter((lesson) => !this.lessonReferenceText(lesson, 'student'));
    const lessonsWithoutInstructorName = lessons.filter((lesson) => !this.lessonReferenceText(lesson, 'instructor'));
    const lessonsWithoutResourceName = lessons.filter((lesson) => !this.lessonReferenceText(lesson, 'resource'));
    const lessonsWithoutCourseName = lessons.filter((lesson) => !this.lessonReferenceText(lesson, 'courseInstance'));
    if (lessonsWithoutStudentName.length > 0) {
      loaders.push(this.dataService.ensureEntitiesLoaded$('students', lessonsWithoutStudentName.map((lesson) => lesson['studentId'])));
    }
    if (lessonsWithoutInstructorName.length > 0) {
      loaders.push(this.dataService.ensureEntitiesLoaded$('instructors', lessonsWithoutInstructorName.map((lesson) => lesson['instructorId'])));
    }
    if (lessonsWithoutResourceName.length > 0) {
      loaders.push(this.dataService.ensureEntitiesLoaded$('resources', lessonsWithoutResourceName.map((lesson) => lesson['resourceId'])));
    }
    if (lessonsWithoutCourseName.length > 0) {
      loaders.push(this.dataService.ensureEntitiesLoaded$('courseInstances', lessonsWithoutCourseName.map((lesson) => lesson['courseInstanceId'])));
    }
    return loaders.length > 0 ? forkJoin(loaders).pipe(map(() => undefined)) : of(undefined);
  }

  private loadPaymentReferences$(payments: DetailEntityRow[]): Observable<void> {
    if (payments.length === 0) {
      return of(undefined);
    }
    return forkJoin([
      this.dataService.ensureEntitiesLoaded$('students', payments.map((payment) => payment['studentId'])),
      this.dataService.ensureEntitiesLoaded$('courseInstances', payments.map((payment) => payment['courseInstanceId']))
    ]).pipe(map(() => undefined));
  }

  private detailTabKey(index: number): string {
    const key = this.entity()?.key;
    if (index === 0 || !key) {
      return 'overview';
    }
    if (key === 'students') {
      return ['overview', 'studentLessons', 'studentPayments', 'studentCourses', 'studentInstructors'][index] ?? 'overview';
    }
    if (key === 'instructors') {
      return [
        'overview',
        'instructorLessons',
        'instructorStudents',
        'instructorCourses',
        'instructorResources',
        'instructorSettlements',
        'instructorRateHistory'
      ][index] ?? 'overview';
    }
    if (key === 'resources') {
      return ['overview', 'resourceCourses', 'relatedLessons'][index] ?? 'overview';
    }
    if (key === 'courseInstances') {
      return ['overview', 'relatedLessons', 'relatedPayments'][index] ?? 'overview';
    }
    return 'overview';
  }

  readonly profileName = computed(() => {
    const row = this.row();
    if (!row) {
      return '';
    }
    const name = this.asString(row['name']);
    const surname = this.asString(row['surname']);
    return `${name} ${surname}`.trim() || this.entityTitle();
  });

  readonly profileSubtitle = computed(() => {
    const key = this.entityKey();
    const row = this.row();
    if (!key || !row) {
      return '';
    }
    if (key === 'students') {
      return this.asString(row['personalIdentificationNumberMasked']) || this.formatStudentBirthDate(row['birthDate']);
    }
    if (key === 'instructors') {
      return this.asString(row['licenceNumber']) || this.asString(row['externalReference']);
    }
    if (key === 'resources') {
      return this.asString(row['registration']) || this.asString(row['externalReference']);
    }
    return this.asString(row['externalReference']);
  });

  readonly profileImageUrl = computed(() => {
    const row = this.row();
    if (!row) {
      return '';
    }
    const value = row['photoUrl'];
    return typeof value === 'string' ? value : '';
  });

  readonly profileInitials = computed(() => {
    const row = this.row();
    if (!row) {
      return '';
    }
    const first = this.asString(row['name']).charAt(0);
    const second = this.asString(row['surname']).charAt(0);
    const fallback = this.entityTitle().charAt(0) || 'R';
    return `${first}${second}`.trim().toUpperCase() || fallback.toUpperCase();
  });

  readonly allFields = computed(() => {
    const row = this.row();
    if (!row) {
      return [];
    }
    if (this.entityKey() === 'courseInstances') {
      return this.courseInstanceOverviewFields(row);
    }
    if (this.entityKey() === 'lessons') {
      return this.lessonOverviewFields(row);
    }
    if (this.entityKey() === 'payments') {
      return this.paymentOverviewFields(row);
    }

    const hiddenKeys = new Set([
      'id',
      'createdAt',
      'updatedAt',
      'organisationId',
      'addressId',
      'contactId',
      'licenceCategoryIds',
      'studentId',
      'studentIds',
      'instructorId',
      'instructorIds',
      'courseId',
      'courseInstanceId',
      'courseInstanceIds',
      'resourceId',
      'resourceIds',
      'defaultResourceId',
      'createdByUserId',
      'createdByDisplay',
      'updatedByUser',
      'updatedByUserId',
      'updatedByDisplay',
      'externalId',
      'legacyData',
      'metadata',
      'invited',
      'credentialsActive',
      'hasPersonalIdentificationNumber',
      'monthlyLessonHoursMonth',
      ...(this.entityKey() === 'resources' ? ['status', ...(this.asStringArray(row['categories']).length ? ['category'] : [])] : []),
      'version',
      'revision'
    ]);
    const fields = Object.entries(row)
      .filter(([key, value]) => !hiddenKeys.has(key) && (key === 'hourlyRateByCategory' || this.isDisplayableOverviewValue(value)))
      .map(([key, value]) => ({
        key,
        label: this.labelForField(key),
        value: this.resolveFieldValue(key, value, row)
      }))
      .filter((item) => item.value.trim().length > 0);

    const keys = new Set(fields.map((item) => item.key));
    if (this.entityKey() === 'students') {
      const existingBirthDateIndex = fields.findIndex((item) => item.key === 'birthDate');
      if (existingBirthDateIndex >= 0) {
        fields.splice(existingBirthDateIndex, 1);
      }
      const birthDateField = {
        key: 'birthDate',
        label: this.labelForField('birthDate'),
        value: this.formatStudentBirthDate(row['birthDate'])
      };
      const maskedPeselIndex = fields.findIndex((item) => item.key === 'personalIdentificationNumberMasked');
      fields.splice(maskedPeselIndex >= 0 ? maskedPeselIndex + 1 : fields.length, 0, birthDateField);
      keys.add('birthDate');
    }
    if ((this.entityKey() === 'students' || this.entityKey() === 'instructors') && !keys.has('accountStatus')) {
      fields.push({
        key: 'accountStatus',
        label: this.labelForField('accountStatus'),
        value: this.resolveFieldValue('accountStatus', null, row)
      });
      keys.add('accountStatus');
    }
    const contact = this.resolveContact(row);
    if (contact) {
      const email = contact['email'];
      const mobile = contact['mobile'] ?? contact['phone'] ?? contact['phoneNumber'] ?? contact['telephone'];
      if (!keys.has('email')) {
        fields.push({
          key: 'email',
          label: this.labelForField('email'),
          value: this.resolveFieldValue('email', email, contact)
        });
        keys.add('email');
      }
      if (!keys.has('mobile')) {
        fields.push({
          key: 'mobile',
          label: this.labelForField('mobile'),
          value: this.resolveFieldValue('mobile', mobile, contact)
        });
        keys.add('mobile');
      }
    }

    const address = this.resolveAddress(row);
    if (address) {
      const addressValues: Record<string, unknown> = {
        streetName: address['streetName'] ?? address['street'],
        buildingNumber: address['buildingNumber'] ?? address['buildingNo'] ?? address['houseNumber'],
        postalCode: address['postalCode'] ?? address['zipCode'],
        city: address['city'],
        country: address['country']
      };
      const addressKeys = ['streetName', 'buildingNumber', 'postalCode', 'city', 'country'];
      for (const key of addressKeys) {
        if (keys.has(key)) {
          continue;
        }
        fields.push({
          key,
          label: this.labelForField(key),
          value: this.resolveFieldValue(key, addressValues[key], address)
        });
        keys.add(key);
      }
    }

    return this.appendAuditFields(fields, row);
  });

  private isDisplayableOverviewValue(value: unknown): boolean {
    if (value == null) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0 && value.every((item) => typeof item !== 'object' || item == null);
    }
    return typeof value !== 'object';
  }

  private courseInstanceOverviewFields(row: DetailEntityRow): Array<{ key: string; label: string; value: string }> {
    const adHoc = this.resolveCourseInstanceType(row) === 'AD_HOC';
    const keys = adHoc
      ? ['name', 'externalReference', 'courseId', 'courseType', 'categories', 'billingModel', 'studentHourlyPrice', 'dateStarted', 'dateCompleted', 'status', 'additionalInformation']
      : ['name', 'externalReference', 'courseId', 'courseType', 'price', 'dateStarted', 'dateCompleted', 'theoryHours', 'practiceHours', 'status', 'additionalInformation'];
    const fields = keys
      .map((key) => ({
        key,
        label: this.courseInstanceFieldLabel(key),
        value: this.resolveFieldValue(key, this.courseInstanceFieldValue(row, key), row)
      }))
      .filter((field) => field.value.trim().length > 0);
    if (adHoc) {
      const lessonSummary = row['lessonsSummary'] as Record<string, unknown> | undefined;
      const paymentSummary = row['payments'] as Record<string, unknown> | undefined;
      if (lessonSummary) {
        const count = this.asNumber(lessonSummary['completedLessonsCount']) + this.asNumber(lessonSummary['plannedLessonsCount']);
        const hours = this.asNumber(lessonSummary['completedPracticeHours']) + this.asNumber(lessonSummary['plannedPracticeHours']);
        fields.push(
          { key: 'adHocLessonsCount', label: this.localization.translate('course.adHoc.lessonsCount'), value: String(count) },
          { key: 'adHocHours', label: this.localization.translate('course.adHoc.hours'), value: this.formatHours(hours) }
        );
      }
      if (paymentSummary) {
        fields.push(
          { key: 'totalPaid', label: this.localization.translate('course.adHoc.totalPaid'), value: this.formatMoney(paymentSummary['totalPaid']) },
          { key: 'totalDue', label: this.localization.translate('course.adHoc.totalDue'), value: this.formatMoney(paymentSummary['totalDue']) },
          { key: 'balance', label: this.localization.translate('course.adHoc.balance'), value: this.formatMoney(paymentSummary['balance']) }
        );
      }
    }
    return this.appendAuditFields(fields, row);
  }

  private courseInstanceFieldLabel(key: string): string {
    const labels: Record<string, string> = {
      courseId: 'common.product',
      theoryHours: 'field.theoreticalLessonsNumber',
      practiceHours: 'field.practicalLessonsNumber'
    };
    const translationKey = labels[key];
    return translationKey ? this.localization.translate(translationKey) : this.labelForField(key);
  }

  private courseInstanceFieldValue(row: DetailEntityRow, key: string): unknown {
    if (key === 'courseType') {
      return this.resolveCourseInstanceType(row);
    }
    if (key === 'billingModel') {
      return row['billingModel'] ?? (this.resolveCourseInstanceType(row) === 'AD_HOC' ? 'HOURLY' : 'FIXED_PRICE');
    }
    if (key === 'theoryHours') {
      return row['theoryHours'] ?? row['theoreticalLessonsNumber'];
    }
    if (key === 'practiceHours') {
      return row['practiceHours'] ?? row['practicalLessonsNumber'];
    }
    return row[key];
  }

  private resolveCourseInstanceType(row: DetailEntityRow): 'STANDARD' | 'AD_HOC' {
    if (row['courseType'] === 'AD_HOC') {
      return 'AD_HOC';
    }
    const course = this.dataService.courses().find((item) => this.sameId(item.id, row['courseId']));
    return course?.courseType === 'AD_HOC' ? 'AD_HOC' : 'STANDARD';
  }

  private lessonOverviewFields(row: DetailEntityRow): Array<{ key: string; label: string; value: string }> {
    const keys = [
      'executionDate',
      'externalReference',
      'lessonTime',
      'durationMinutes',
      'status',
      'studentName',
      'instructorName',
      'resourceName',
      'courseInstanceName',
      'category',
      'additionalInformation',
      'studentComment',
      'instructorPrivateNote',
      'cancellationReason',
      'cancellationReasonType',
      'cancelledBy',
      'cancelledAt',
      'cancellationConsequence',
      'studentCancellationChargeAmount'
    ];
    const fields = keys
      .map((key) => ({
        key,
        label: this.lessonFieldLabel(key),
        value: this.resolveFieldValue(key, this.lessonFieldValue(row, key), row)
      }))
      .filter((field) => field.value.trim().length > 0);
    return this.appendAuditFields(fields, row);
  }

  private paymentOverviewFields(row: DetailEntityRow): Array<{ key: string; label: string; value: string }> {
    const keys = ['externalReference', 'studentId', 'courseInstanceId', 'amount', 'paymentDate', 'paidTo', 'paymentType', 'status', 'additionalInformation'];
    const fields = keys
      .map((key) => ({
        key,
        label: key === 'courseInstanceId' ? this.localization.translate('common.course') : this.labelForField(key),
        value: this.resolveFieldValue(key, row[key], row)
      }))
      .filter((field) => field.value.trim().length > 0);
    return this.appendAuditFields(fields, row);
  }

  private appendAuditFields(
    fields: Array<{ key: string; label: string; value: string }>,
    row: DetailEntityRow
  ): Array<{ key: string; label: string; value: string }> {
    const auditFields = ['createdAt', 'updatedAt']
      .map((key) => ({
        key,
        label: this.labelForField(key),
        value: this.resolveFieldValue(key, row[key], row)
      }))
      .filter((field) => field.value.trim().length > 0);
    return [...fields, ...auditFields];
  }

  private lessonFieldLabel(key: string): string {
    const labels: Record<string, string> = {
      studentName: 'common.student',
      instructorName: 'common.instructor',
      resourceName: 'common.resource',
      courseInstanceName: 'common.course',
      category: 'common.category'
    };
    const translationKey = labels[key];
    return translationKey ? this.localization.translate(translationKey) : this.labelForField(key);
  }

  private lessonFieldValue(row: DetailEntityRow, key: string): unknown {
    if (key === 'studentName') {
      return this.firstText(row['studentDisplayName'], row['studentName'], this.embeddedReferenceValue(row, 'studentId'), row['studentId']);
    }
    if (key === 'instructorName') {
      return this.firstText(row['instructorDisplayName'], row['instructorName'], this.embeddedReferenceValue(row, 'instructorId'), row['instructorId']);
    }
    if (key === 'resourceName') {
      return this.firstText(row['resourceDisplayName'], row['resourceName'], this.embeddedReferenceValue(row, 'resourceId'), row['resourceId']);
    }
    if (key === 'courseInstanceName') {
      return this.firstText(
        row['courseInstanceDisplayName'],
        row['courseInstanceName'],
        this.embeddedReferenceValue(row, 'courseInstanceId'),
        row['courseInstanceId']
      );
    }
    return row[key];
  }

  private resolveContact(row: Record<string, unknown>): Record<string, unknown> | null {
    const contactId = row['contactId'] ?? null;
    if (contactId !== null) {
      const contact = this.dataService.contacts().find((item) => this.sameId(item.id, contactId));
      if (contact) {
        return contact as unknown as Record<string, unknown>;
      }
    }
    return this.asRecord(row['contact']);
  }

  private resolveAddress(row: Record<string, unknown>): Record<string, unknown> | null {
    const addressId = row['addressId'] ?? null;
    if (addressId !== null) {
      const address = this.dataService.addresses().find((item) => this.sameId(item.id, addressId));
      if (address) {
        return address as unknown as Record<string, unknown>;
      }
    }
    return this.asRecord(row['address']);
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
  }

  readonly hasStructuredOverview = computed(() => this.entityKey() === 'students' || this.entityKey() === 'instructors');

  readonly overviewSections = computed<OverviewSection[]>(() => {
    const fields = this.allFields();
    if (fields.length === 0) {
      return [];
    }

    const fieldByKey = new Map(fields.map((item) => [item.key, item]));
    const contactKeys = new Set(['email', 'mobile']);
    const addressKeys = new Set(['streetName', 'buildingNumber', 'postalCode', 'city', 'country']);
    const contactOrder = ['email', 'mobile'];
    const addressOrder = ['streetName', 'buildingNumber', 'postalCode', 'city', 'country'];

    const basicFields = fields.filter((item) => !contactKeys.has(item.key) && !addressKeys.has(item.key));
    const contactFields = contactOrder.map((key) => fieldByKey.get(key) ?? {
      key,
      label: this.labelForField(key),
      value: '-'
    });
    const addressFields = addressOrder.map((key) => fieldByKey.get(key) ?? {
      key,
      label: this.labelForField(key),
      value: '-'
    });

    return [
      {
        key: 'basic',
        label: this.localization.translate('createDialog.section.basic'),
        fields: basicFields
      },
      {
        key: 'contact',
        label: this.localization.translate('createDialog.section.contact'),
        fields: contactFields
      },
      {
        key: 'address',
        label: this.localization.translate('createDialog.section.address'),
        fields: addressFields
      }
    ];
  });

  readonly studentStats = computed(() => {
    if (this.entityKey() !== 'students' || !this.row()) {
      return null;
    }
    const student = this.row()!;
    const studentId = student.id;
    const courses = this.studentCourses();
    const paymentBalance = this.getPaymentBalance(student);
    const baseCourseValue =
      paymentBalance?.totalDue ?? courses.reduce((sum, item) => sum + this.asNumber(item['price']), 0);
    const paid =
      paymentBalance?.totalPaid ??
      paymentBalance?.paidAmount ??
      this.studentPayments().reduce((sum, item) => sum + this.asNumber(item['amount']), 0);
    const aggregatedProgress = this.aggregateCourseProgress(courses);
    const practiceDone =
      aggregatedProgress.completed ??
      paymentBalance?.completedPracticeHours ??
      this.studentLessons().reduce((sum, item) => sum + this.asNumber(item['durationMinutes']), 0) / 60;
    const practicePlanned =
      aggregatedProgress.required ??
      paymentBalance?.totalRequiredPracticeHours ??
      courses.reduce((sum, item) => sum + this.asNumber(item['practiceHours'] ?? item['practicalLessonsNumber']), 0);
    const theoryPlanned = courses.reduce((sum, item) => sum + this.asNumber(item['theoryHours'] ?? item['theoreticalLessonsNumber']), 0);
    const theoryDone = courses.reduce((sum, item) => sum + this.asNumber((item as unknown as Record<string, unknown>)['theoryHoursDone']), 0);
    const averageHourlyRate = paymentBalance?.averageHourlyRate ?? (practicePlanned > 0 ? baseCourseValue / practicePlanned : 0);
    const usedFunds = paymentBalance?.usedFunds ?? practiceDone * averageHourlyRate;
    const totalCourseValue = Math.max(baseCourseValue, usedFunds);
    const amountDue = Math.max(
      totalCourseValue - paid,
      paymentBalance?.remainingAmount ?? 0,
      -(paymentBalance?.rideBalance ?? 0),
      0
    );
    const practiceProgress = practicePlanned > 0 ? Math.min(100, Math.round((practiceDone / practicePlanned) * 100)) : 0;
    const theoryProgress = theoryPlanned > 0 ? Math.min(100, Math.round((theoryDone / theoryPlanned) * 100)) : 0;
    return {
      studentId,
      paid,
      totalCourseValue,
      balance: amountDue,
      practiceDone: Number(practiceDone.toFixed(1)),
      practicePlanned,
      practiceProgress,
      theoryDone,
      theoryPlanned,
      theoryProgress
    };
  });

  readonly instructorStats = computed(() => {
    if (this.entityKey() !== 'instructors' || !this.row()) {
      return null;
    }
    const currentMonthToken = new Date().toISOString().slice(0, 7);
    const settlements = this.instructorSettlements();
    const currentMonth = settlements.find((item) => item.month === currentMonthToken);
    const latestMonth = settlements.slice().sort((a, b) => b.month.localeCompare(a.month))[0];
    const period = currentMonth ?? latestMonth;
    const bookedHours = this.asNumber(period?.plannedDurationHours);
    const completedHours = this.asNumber(period?.completedDurationHours);
    const payableCancelledHours = (period?.categories ?? []).reduce(
      (sum, category) => sum + this.asNumber(category.payableCancelledDurationHours),
      0
    );
    return {
      projectedSalary: this.asNumber(period?.projectedPayoutAmount),
      bookedHours: Number(bookedHours.toFixed(1)),
      completedHours: Number(completedHours.toFixed(1)),
      payableCancelledHours: Number(payableCancelledHours.toFixed(1)),
      monthLessons: this.asNumber(period?.lessonsCount),
    };
  });

  readonly studentLessons = computed<DetailEntityRow[]>(() => {
    if (this.entityKey() !== 'students' || !this.row()) {
      return [];
    }
    return this.studentLessonsPage().items;
  });

  readonly studentPayments = computed<DetailEntityRow[]>(() => {
    if (this.entityKey() !== 'students' || !this.row()) {
      return [];
    }
    return this.studentPaymentsPage().items;
  });

  readonly studentCourses = computed<CourseInstance[]>(() => {
    if (this.entityKey() !== 'students' || !this.row()) {
      return [];
    }
    return this.studentCoursesPage().items as unknown as CourseInstance[];
  });

  readonly studentInstructors = computed(() => {
    if (this.entityKey() !== 'students' || !this.row()) {
      return [];
    }
    return this.studentInstructorsPage().items;
  });

  readonly availableInstructorsForStudent = computed(() => {
    return [];
  });

  readonly availableCoursesForStudent = computed(() => {
    return [];
  });

  readonly availableResourceAssignmentsForInstructor = computed<InstructorResourceAssignmentOption[]>(() => {
    if (this.entityKey() !== 'instructors' || !this.row()) {
      return [];
    }

    const assignedIds = this.asNumberArray(this.row()!['resourceIds']);
    return this.dataService.resources()
      .filter((resource) => !assignedIds.some((id) => this.sameId(id, resource.id)))
      .map((resource) => ({ id: resource.id, resourceId: resource.id, label: resource.name }));
  });

  readonly instructorLessons = computed<DetailEntityRow[]>(() => {
    if (this.entityKey() !== 'instructors' || !this.row()) {
      return [];
    }
    return this.instructorLessonsPage().items;
  });

  readonly instructorLessonStudentOptions = computed(() => {
    const map = new Map<string | number, string>();
    this.instructorLessons().forEach((lesson) => {
      const student = this.dataService.students().find((item) => this.sameId(item.id, lesson['studentId']));
      if (!student) {
        return;
      }
      map.set(student.id, `${student.name} ${student.surname}`);
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  readonly hasInstructorLessonsFilter = computed(
    () =>
      this.instructorLessonDateFrom().length > 0 ||
      this.instructorLessonDateTo().length > 0 ||
      this.instructorLessonStudentId() != null ||
      this.instructorLessonsQuickFilter() !== 'all'
  );

  readonly instructorLessonsFilterSummary = computed(() => {
    const parts: string[] = [];
    if (this.instructorLessonsQuickFilter() !== 'all') {
      parts.push(
        this.instructorLessonsQuickFilter() === 'completed'
          ? this.localization.translate('status.completed')
          : this.localization.translate('status.planned')
      );
    }
    if (this.instructorLessonDateFrom()) {
      parts.push(`${this.localization.translate('common.from')} ${this.instructorLessonDateFrom()}`);
    }
    if (this.instructorLessonDateTo()) {
      parts.push(`${this.localization.translate('common.to')} ${this.instructorLessonDateTo()}`);
    }
    if (this.instructorLessonStudentId() != null) {
      const selected = this.instructorLessonStudentOptions().find((item) => this.sameId(item.id, this.instructorLessonStudentId()));
      if (selected) {
        parts.push(`${this.localization.translate('common.student')} ${selected.label}`);
      }
    }
    return parts.join(', ');
  });

  readonly filteredInstructorLessons = computed<DetailEntityRow[]>(() => {
    return this.instructorLessons();
  });

  readonly instructorLessonsTotalItems = computed(() => this.instructorLessonsPage().total);
  readonly instructorLessonsPageRows = computed(() => this.instructorLessonsPage().items);

  readonly instructorStudents = computed(() => {
    if (this.entityKey() !== 'instructors' || !this.row()) {
      return [];
    }
    return this.instructorStudentsPage().items;
  });
  readonly instructorStudentsTotalItems = computed(() => this.instructorStudentsPage().total);

  readonly instructorCourses = computed(() => {
    if (this.entityKey() !== 'instructors' || !this.row()) {
      return [];
    }
    return this.instructorCoursesPage().items;
  });

  readonly instructorResources = computed(() => {
    if (this.entityKey() !== 'instructors' || !this.row()) {
      return [];
    }
    const assignedIds = this.asNumberArray(this.row()!['resourceIds']);
    return this.instructorResourcesPage().items.filter((resource) =>
      assignedIds.some((id) => this.sameId(id, resource.id))
    );
  });

  readonly availableCoursesForInstructor = computed(() => {
    return [];
  });

  readonly resourceCourses = computed(() => {
    if (this.entityKey() !== 'resources' || !this.row()) {
      return [];
    }
    return this.resourceCoursesPage().items;
  });

  readonly availableCoursesForResource = computed(() => {
    return [];
  });

  readonly relatedLessons = computed(() => {
    const key = this.entityKey();
    const row = this.row();
    if (!key || !row) {
      return [];
    }
    return this.relatedLessonsPage().items;
  });

  readonly relatedPayments = computed(() => {
    if (this.entityKey() !== 'courseInstances' || !this.row()) {
      return [];
    }
    return this.relatedPaymentsPage().items;
  });

  readonly embeddedLessonsTotal = computed(() => this.entityKey() === 'students' ? this.studentLessonsPage().total : this.relatedLessonsPage().total);
  readonly embeddedPaymentsTotal = computed(() => this.entityKey() === 'students' ? this.studentPaymentsPage().total : this.relatedPaymentsPage().total);

  onEmbeddedLessonsPageChange(event: PageEvent): void {
    this.embeddedLessonsPageIndex.set(event.pageIndex);
    this.embeddedLessonsPageSize.set(event.pageSize);
  }

  onEmbeddedPaymentsPageChange(event: PageEvent): void {
    this.embeddedPaymentsPageIndex.set(event.pageIndex);
    this.embeddedPaymentsPageSize.set(event.pageSize);
  }

  setEmbeddedDateFilter(kind: 'lessons' | 'payments', boundary: 'from' | 'to', value: string): void {
    const target = kind === 'lessons'
      ? (boundary === 'from' ? this.embeddedLessonsDateFrom : this.embeddedLessonsDateTo)
      : (boundary === 'from' ? this.embeddedPaymentsDateFrom : this.embeddedPaymentsDateTo);
    target.set(value);
    (kind === 'lessons' ? this.embeddedLessonsPageIndex : this.embeddedPaymentsPageIndex).set(0);
  }

  clearEmbeddedFilters(kind: 'lessons' | 'payments'): void {
    if (kind === 'lessons') {
      this.embeddedLessonsDateFrom.set('');
      this.embeddedLessonsDateTo.set('');
      this.embeddedLessonsPageIndex.set(0);
      return;
    }
    this.embeddedPaymentsDateFrom.set('');
    this.embeddedPaymentsDateTo.set('');
    this.embeddedPaymentsPageIndex.set(0);
  }

  readonly instructorRateHistory = computed(() => {
    if (this.entityKey() !== 'instructors' || !this.row()) {
      return [];
    }
    return this.instructorRateHistoryPage().items;
  });

  readonly detailsTabLabels = computed(() => {
    const labels: string[] = [this.localization.translate('resourceDetails.overview')];
    const key = this.entityKey();
    if (key === 'students') {
      labels.push(
        this.localization.translate('resourceDetails.lessons'),
        this.localization.translate('resourceDetails.payments'),
        this.localization.translate('resourceDetails.courses'),
        this.localization.translate('resourceDetails.instructors')
      );
      return labels;
    }
    if (key === 'instructors') {
      labels.push(
        this.localization.translate('resourceDetails.lessons'),
        this.localization.translate('common.students'),
        this.localization.translate('resourceDetails.courses'),
        this.localization.translate('entity.resources'),
        this.localization.translate('resourceDetails.settlements'),
        this.localization.translate('resourceDetails.rateHistory')
      );
      return labels;
    }
    if (key === 'resources') {
      labels.push(this.localization.translate('resourceDetails.courses'));
    }
    if (key === 'resources' || key === 'courseInstances') {
      labels.push(this.localization.translate('resourceDetails.lessons'));
    }
    if (key === 'courseInstances') {
      labels.push(this.localization.translate('resourceDetails.payments'));
    }
    return labels;
  });

  readonly studentLessonColumns = [
    'executionDate',
    'lessonTime',
    'durationMinutes',
    'instructorId',
    'resourceId',
    'cancellationSummary',
    'studentComment'
  ];
  readonly studentPaymentColumns = ['paymentDate', 'courseInstanceId', 'amount', 'paymentType', 'paidTo', 'actions'];
  readonly studentCourseColumns = [
    'name',
    'price',
    'cancellationChargeAmount',
    'totalDue',
    'lessonsNumber',
    'dateStarted',
    'progress',
    'status',
    'actions'
  ];
  readonly studentInstructorColumns = ['name', 'surname', 'licenceNumber', 'status', 'relationActions'];
  readonly instructorLessonColumns = [
    'executionDate',
    'lessonTime',
    'durationMinutes',
    'studentId',
    'resourceId',
    'cancellationSummary',
    'studentComment',
    'instructorPrivateNote'
  ];
  readonly instructorStudentColumns = ['name', 'surname', 'birthDate', 'status', 'relationActions'];
  readonly instructorCourseColumns = ['name', 'price', 'lessonsNumber', 'dateStarted', 'status', 'relationActions'];
  readonly instructorResourceColumns = ['name', 'registration', 'category', 'defaultResource', 'resourceActions'];
  readonly instructorSettlementColumns = [
    'monthKey',
    'settlementStatus',
    'grossPayoutAmount',
    'cashCollectedAmount',
    'netPayoutAmount',
    'settledByDisplay',
    'totalHours',
    'missingRateWarning'
  ];
  readonly instructorSettlementCategoryColumns = [
    'categoryName',
    'totalHours',
    'payableCancelledDurationHours',
    'hourlyRate',
    'payoutAmount',
    'completedPayoutAmount',
    'plannedPayoutAmount'
  ];
  readonly instructorRateHistoryColumns = [
    'createdAt',
    'changedByDisplay',
    'previousHourlyRateByCategory',
    'newHourlyRateByCategory',
    'changeReason'
  ];
  readonly instructorSettlementPaymentColumns = ['paymentDate', 'studentName', 'courseInstanceName', 'amount', 'paymentType', 'status'];
  readonly resourceCourseColumns = ['name', 'price', 'lessonsNumber', 'dateStarted', 'status', 'relationActions'];
  readonly genericLessonColumns = ['executionDate', 'lessonTime', 'durationMinutes', 'studentId', 'instructorId', 'cancellationSummary'];
  readonly genericPaymentColumns = ['paymentDate', 'amount', 'paymentType', 'studentId'];
  readonly mobileColumns = ['mobileLeft', 'mobileRight'];
  readonly canAddLesson = this.userContext.canAddLesson;
  readonly canAddPayment = this.userContext.canAddPayment;
  readonly canAssignStudentRelations = this.userContext.canAssignStudentRelations;
  readonly canEditResources = this.userContext.canEditResources;
  readonly canUseInstructorCalendar = this.userContext.canUseInstructorCalendar;
  readonly canUseResourceCalendar = this.userContext.canUseResourceCalendar;
  readonly canSeeInstructorPayoutProjection = this.userContext.canSeeInstructorPayoutProjection;
  readonly canEnterUserAppContext = computed(() => this.auth.isRoot() || this.auth.isDelegatedOrganisationSession());
  readonly invitingInstructor = signal(false);
  readonly instructorSettlementRows = computed(() => {
    if (this.entityKey() !== 'instructors' || !this.row()) {
      return [];
    }
    return this.instructorSettlements()
      .filter((item) => this.isCurrentOrPastMonth(item.month))
      .map((item) => {
        const categoryRows = this.instructorSettlementCategoryRows(item);
        const grossPayoutAmount = item.grossPayoutAmount ?? item.settlement?.grossPayoutAmount ?? item.completedPayoutAmount;
        const cashCollectedAmount = item.cashCollectedAmount ?? item.settlement?.cashCollectedAmount ?? 0;
        const netPayoutAmount = item.netPayoutAmount ?? item.settlement?.netPayoutAmount ?? grossPayoutAmount - cashCollectedAmount;
        const skippedWithoutRate = this.asRecord((this.asRecord(item) ?? {})['skippedWithoutRate']);
        const skippedWithoutRateLessonsCount = this.asNumber(skippedWithoutRate?.['lessonsCount']);
        return {
          monthKey: item.month,
          settlementStatus: item.settlement ? this.localization.translate('settlement.settled') : this.localization.translate('settlement.unsettled'),
          settlementAmount: item.settlement?.totalAmount ?? null,
          grossPayoutAmount,
          cashCollectedAmount,
          netPayoutAmount,
          settledByDisplay: item.settlement?.settledByDisplay ?? '',
          totalHours: Number(this.asNumber(item.durationHours).toFixed(1)),
          payoutAmount: this.asNumber(item.projectedPayoutAmount),
          completedPayoutAmount: this.asNumber(item.completedPayoutAmount),
          plannedPayoutAmount: this.asNumber(item.plannedPayoutAmount),
          settleableLessonsCount: this.settleableLessonsCount(item),
          hasSettlement: item.settlement != null,
          missingRateWarning: skippedWithoutRateLessonsCount > 0
            ? this.localization.translate('settlement.missingRateWarning')
            : '',
          paymentsCollected: item.paymentsCollected ?? [],
          categories: categoryRows
        };
      })
      .sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  });

  private instructorSettlementCategoryRows(item: InstructorSettlementMonth): Array<Record<string, unknown>> {
    const categories = item.categories?.length
      ? item.categories
      : [
          {
            category: this.localization.translate('common.all'),
            lessonsCount: item.lessonsCount,
            completedLessonsCount: item.completedLessonsCount,
            plannedLessonsCount: item.plannedLessonsCount,
            cancelledLessonsCount: item.cancelledLessonsCount,
            durationMinutes: item.durationMinutes,
            completedDurationMinutes: item.completedDurationMinutes,
            plannedDurationMinutes: item.plannedDurationMinutes,
            durationHours: item.durationHours,
            completedDurationHours: item.completedDurationHours,
            plannedDurationHours: item.plannedDurationHours,
            hourlyRate: 0,
            projectedPayoutAmount: item.projectedPayoutAmount,
            completedPayoutAmount: item.completedPayoutAmount,
            plannedPayoutAmount: item.plannedPayoutAmount
          }
        ];

    return categories.map((category) => ({
      categoryName: category.category,
      totalHours: Number(this.asNumber(category.durationHours).toFixed(1)),
      payableCancelledDurationHours: Number(this.asNumber(category.payableCancelledDurationHours).toFixed(1)),
      hourlyRate: category.hourlyRate,
      payoutAmount: this.asNumber(category.projectedPayoutAmount),
      completedPayoutAmount: this.asNumber(category.completedPayoutAmount),
      plannedPayoutAmount: this.asNumber(category.plannedPayoutAmount)
    }));
  }

  settlementCategoryRows(row: Record<string, unknown>): Array<Record<string, unknown>> {
    return Array.isArray(row['categories']) ? (row['categories'] as Array<Record<string, unknown>>) : [];
  }

  settlementPaymentRows(row: Record<string, unknown>): Array<Record<string, unknown>> {
    return Array.isArray(row['paymentsCollected']) ? (row['paymentsCollected'] as Array<Record<string, unknown>>) : [];
  }

  toggleSettlementMonth(row: Record<string, unknown>): void {
    const monthKey = `${row['monthKey'] ?? ''}`;
    this.expandedSettlementMonth.set(this.expandedSettlementMonth() === monthKey ? null : monthKey);
  }

  isSettlementMonthExpanded(row: Record<string, unknown>): boolean {
    return this.expandedSettlementMonth() === `${row['monthKey'] ?? ''}`;
  }

  canSettleMonth(row: Record<string, unknown>): boolean {
    const month = `${row['monthKey'] ?? ''}`.trim();
    return (
      this.canEditResources() &&
      row['hasSettlement'] !== true &&
      month.length > 0 &&
      this.isCurrentOrPastMonth(month) &&
      this.asNumber(row['settleableLessonsCount']) > 0
    );
  }

  settleInstructorMonth(row: Record<string, unknown>, event: Event): void {
    event.stopPropagation();
    const instructor = this.row();
    const month = `${row['monthKey'] ?? ''}`.trim();
    if (!instructor || !month || !this.canSettleMonth(row)) {
      return;
    }
    this.confirmImmediateAction(this.localization.translate('settlement.settleMonth'), () => {
      this.settlingMonth.set(month);
      this.dataService.settleInstructorMonth$(instructor.id, month).subscribe({
      next: (settlement) => {
        this.instructorSettlementsRefresh.update((value) => value + 1);
        const skipped = settlement.skippedWithoutRate?.lessonsCount ?? 0;
        if (skipped > 0) {
          this.snackBar.open(
            this.localization.translate('settlement.skippedWithoutRate', {
              count: skipped,
              hours: Number(this.asNumber(settlement.skippedWithoutRate.durationHours).toFixed(1))
            }),
            this.localization.translate('common.close'),
            { duration: 8000, panelClass: ['app-snackbar-error'] }
          );
        }
      },
      error: (error: unknown) => {
        this.snackBar.open(this.settlementErrorMessage(error), this.localization.translate('common.close'), {
          duration: 7000,
          panelClass: ['app-snackbar-error']
        });
      },
        complete: () => this.settlingMonth.set(null)
      });
    }, month);
  }

  private settleableLessonsCount(item: InstructorSettlementMonth): number {
    const row = this.asRecord(item) ?? {};
    const payableCancelledLessonsCount =
      this.asNumber(row['payableCancelledLessonsCount']) ||
      this.asNumber(row['cancelledWithConsequencesLessonsCount']) ||
      this.asNumber(row['withConsequencesCancelledLessonsCount']);
    return (
      this.asNumber(row['settleableLessonsCount']) ||
      this.asNumber(row['payableLessonsCount']) ||
      this.asNumber(item.completedLessonsCount) + payableCancelledLessonsCount ||
      this.asNumber(item.lessonsCount) ||
      this.asNumber(item.completedLessonsCount) + this.asNumber(item.cancelledLessonsCount)
    );
  }

  private isCurrentOrPastMonth(month: string): boolean {
    return /^\d{4}-\d{2}$/.test(month) && month <= new Date().toISOString().slice(0, 7);
  }

  private settlementErrorMessage(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code === 'FUTURE_SETTLEMENT_MONTH') {
      return this.localization.translate('settlement.error.futureMonth');
    }
    if (code === 'NO_SETTLEMENT_LESSONS') {
      return this.localization.translate('settlement.error.noLessons');
    }
    return this.localization.translate('settlement.settleFailed');
  }

  private extractErrorCode(error: unknown): string {
    const payload = error instanceof HttpErrorResponse ? error.error : error;
    const record = this.asRecord(payload) ?? {};
    return `${record['code'] ?? record['errorCode'] ?? record['error'] ?? ''}`.trim();
  }

  constructor() {
    effect(() => {
      this.instructorLessonDateFrom();
      this.instructorLessonDateTo();
      this.instructorLessonStudentId();
      this.instructorLessonsQuickFilter();
      this.instructorLessonsPageIndex.set(0);
    });
    effect(() => {
      this.entityKey();
      this.id();
      this.detailsTabIndex.set(0);
      this.embeddedLessonsPageIndex.set(0);
      this.embeddedPaymentsPageIndex.set(0);
      this.embeddedLessonsDateFrom.set('');
      this.embeddedLessonsDateTo.set('');
      this.embeddedPaymentsDateFrom.set('');
      this.embeddedPaymentsDateTo.set('');
    });
    effect(() => {
      if (this.entityKey() === 'students' && this.row()?.id) {
        this.dataService.loadCourseInstancesForStudent(this.row()!.id);
      }
    });
  }

  getMobileHeader(tableKey: string, column: 'mobileLeft' | 'mobileRight'): string {
    const map: Record<string, { left: string; right: string; leftFallbackKey: string; rightFallbackKey: string }> = {
      studentLessons: { left: 'executionDate', right: 'instructorId', leftFallbackKey: 'common.date', rightFallbackKey: 'common.instructor' },
      studentPayments: { left: 'paymentDate', right: 'paymentType', leftFallbackKey: 'common.date', rightFallbackKey: 'field.type' },
      studentCourses: { left: 'name', right: 'status', leftFallbackKey: 'common.course', rightFallbackKey: 'common.status' },
      studentInstructors: { left: 'name', right: 'status', leftFallbackKey: 'common.instructor', rightFallbackKey: 'common.status' },
      instructorLessons: { left: 'executionDate', right: 'studentId', leftFallbackKey: 'common.date', rightFallbackKey: 'common.student' },
      instructorStudents: { left: 'name', right: 'status', leftFallbackKey: 'common.student', rightFallbackKey: 'common.status' },
      instructorCourses: { left: 'name', right: 'status', leftFallbackKey: 'common.course', rightFallbackKey: 'common.status' },
      instructorResources: { left: 'name', right: 'category', leftFallbackKey: 'common.resource', rightFallbackKey: 'common.category' },
      instructorSettlements: { left: 'monthKey', right: 'payoutAmount', leftFallbackKey: 'field.monthKey', rightFallbackKey: 'field.payoutAmount' },
      instructorRateHistory: { left: 'createdAt', right: 'newHourlyRateByCategory', leftFallbackKey: 'field.createdAt', rightFallbackKey: 'field.newHourlyRateByCategory' },
      genericLessons: { left: 'executionDate', right: 'studentId', leftFallbackKey: 'common.date', rightFallbackKey: 'common.student' },
      genericPayments: { left: 'paymentDate', right: 'paymentType', leftFallbackKey: 'common.date', rightFallbackKey: 'field.type' }
    };
    const target = map[tableKey];
    if (!target) {
      return this.localization.translate(column === 'mobileLeft' ? 'common.info' : 'root.common.details');
    }
    const key = column === 'mobileLeft' ? target.left : target.right;
    const fallbackKey = column === 'mobileLeft' ? target.leftFallbackKey : target.rightFallbackKey;
    return this.getColumnLabel(key) || this.localization.translate(fallbackKey);
  }

  getMobileLeftLines(tableKey: string, row: Record<string, unknown>): string[] {
    const lines: string[] = [];
    if (tableKey === 'studentLessons' || tableKey === 'instructorLessons' || tableKey === 'genericLessons') {
      this.pushLine(lines, this.getValue(row, 'executionDate'));
      this.pushLine(lines, `${this.getValue(row, 'lessonTime')} (${this.getValue(row, 'durationMinutes')})`);
      return lines;
    }
    if (tableKey === 'studentPayments' || tableKey === 'genericPayments') {
      this.pushLine(lines, this.getValue(row, 'paymentDate'));
      this.pushLine(lines, this.getValue(row, 'amount'));
      return lines;
    }
    if (tableKey === 'studentCourses' || tableKey === 'instructorCourses') {
      this.pushLine(lines, this.getValue(row, 'name'));
      this.pushLine(lines, `${this.getValue(row, 'price')} | ${this.getValue(row, 'lessonsNumber')}`);
      return lines;
    }
    if (tableKey === 'studentInstructors') {
      this.pushLine(lines, `${this.getValue(row, 'name')} ${this.getValue(row, 'surname')}`.trim());
      this.pushLine(lines, this.getValue(row, 'licenceNumber'));
      return lines;
    }
    if (tableKey === 'instructorStudents') {
      this.pushLine(lines, `${this.getValue(row, 'name')} ${this.getValue(row, 'surname')}`.trim());
      this.pushLine(lines, this.getValue(row, 'birthDate'));
      return lines;
    }
    if (tableKey === 'instructorResources') {
      this.pushLine(lines, this.getValue(row, 'name'));
      this.pushLine(lines, this.getValue(row, 'registration'));
      return lines;
    }
    if (tableKey === 'instructorSettlements') {
      this.pushLine(lines, this.getValue(row, 'monthKey'));
      this.pushLine(lines, this.getValue(row, 'totalHours'));
      return lines;
    }
    if (tableKey === 'instructorRateHistory') {
      this.pushLine(lines, this.getValue(row, 'createdAt'));
      this.pushLine(lines, this.getValue(row, 'changedByDisplay'));
      return lines;
    }
    this.pushLine(lines, this.getValue(row, 'name'));
    return lines;
  }

  getMobileRightLines(tableKey: string, row: Record<string, unknown>): string[] {
    const lines: string[] = [];
    if (tableKey === 'studentLessons') {
      this.pushLine(lines, this.getValue(row, 'instructorId'));
      this.pushLine(lines, this.getValue(row, 'status'));
      this.pushLine(lines, this.getValue(row, 'studentComment'));
      return lines;
    }
    if (tableKey === 'studentPayments') {
      this.pushLine(lines, this.getValue(row, 'courseInstanceId'));
      this.pushLine(lines, this.getValue(row, 'paymentType'));
      this.pushLine(lines, this.getValue(row, 'paidTo'));
      return lines;
    }
    if (tableKey === 'studentCourses') {
      this.pushLine(lines, this.getValue(row, 'status'));
      this.pushLine(lines, this.getValue(row, 'progress'));
      this.pushLine(lines, this.getValue(row, 'dateStarted'));
      return lines;
    }
    if (tableKey === 'studentInstructors') {
      this.pushLine(lines, this.getValue(row, 'status'));
      return lines;
    }
    if (tableKey === 'instructorLessons') {
      this.pushLine(lines, this.getValue(row, 'studentId'));
      this.pushLine(lines, this.getValue(row, 'status'));
      this.pushLine(lines, this.getValue(row, 'studentComment'));
      return lines;
    }
    if (tableKey === 'instructorStudents' || tableKey === 'instructorCourses') {
      this.pushLine(lines, this.getValue(row, 'status'));
      return lines;
    }
    if (tableKey === 'instructorResources') {
      this.pushLine(lines, this.getValue(row, 'category'));
      return lines;
    }
    if (tableKey === 'instructorSettlements') {
      this.pushLine(lines, this.getValue(row, 'payoutAmount'));
      this.pushLine(lines, this.getValue(row, 'completedPayoutAmount'));
      this.pushLine(lines, this.getValue(row, 'plannedPayoutAmount'));
      return lines;
    }
    if (tableKey === 'instructorRateHistory') {
      this.pushLine(lines, this.getValue(row, 'newHourlyRateByCategory'));
      this.pushLine(lines, this.getValue(row, 'changeReason'));
      return lines;
    }
    if (tableKey === 'genericLessons') {
      this.pushLine(lines, this.getValue(row, 'studentId'));
      this.pushLine(lines, this.getValue(row, 'instructorId'));
      return lines;
    }
    if (tableKey === 'genericPayments') {
      this.pushLine(lines, this.getValue(row, 'paymentType'));
      this.pushLine(lines, this.getValue(row, 'studentId'));
      return lines;
    }
    return lines;
  }

  private pushLine(lines: string[], value: string): void {
    const normalized = `${value ?? ''}`.trim();
    lines.push(normalized.length > 0 ? normalized : '-');
  }

  onDetailsTabIndexChange(index: number): void {
    this.detailsTabIndex.set(index);
  }

  previousDetailsTab(): void {
    this.detailsTabIndex.update((value) => Math.max(0, value - 1));
  }

  nextDetailsTab(): void {
    const maxIndex = Math.max(this.detailsTabLabels().length - 1, 0);
    this.detailsTabIndex.update((value) => Math.min(maxIndex, value + 1));
  }

  isFirstDetailsTab(): boolean {
    return this.detailsTabIndex() <= 0;
  }

  isLastDetailsTab(): boolean {
    return this.detailsTabIndex() >= this.detailsTabLabels().length - 1;
  }

  activeDetailsTabLabel(): string {
    const labels = this.detailsTabLabels();
    const index = this.detailsTabIndex();
    return labels[index] ?? labels[0] ?? '';
  }

  nextDetailsTabLabel(): string {
    const labels = this.detailsTabLabels();
    const index = this.detailsTabIndex() + 1;
    return labels[index] ?? '';
  }

  getValue(row: Record<string, unknown>, key: string): string {
    return this.resolveFieldValue(key, row[key], row);
  }

  canLinkToDetails(row: Record<string, unknown>, key: string): boolean {
    return this.getLinkedTargetForCell(row, key) !== null;
  }

  openLinkedDetails(row: Record<string, unknown>, key: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const target = this.getLinkedTargetForCell(row, key);
    if (!target) {
      return;
    }
    this.router.navigate(['/resources', target.entityKey, target.id], { queryParamsHandling: 'preserve' });
  }

  getColumnLabel(column: string): string {
    if (column === 'actions' || column === 'relationActions') {
      const translatedActions = this.localization.translate('common.actions');
      return translatedActions === 'common.actions' ? 'Akcje' : translatedActions;
    }
    if (column === 'amount') {
      return this.localization.translate('paymentDialog.amount');
    }
    if (column === 'paymentType') {
      return this.localization.translate('paymentDialog.paymentType');
    }
    if (column === 'paidTo') {
      return this.localization.translate('paymentDialog.collectedBy');
    }
    if (column === 'courseInstanceId') {
      return this.localization.translate('common.course');
    }
    const keyMap: Record<string, string> = {
      student: 'common.student',
      instructor: 'common.instructor',
      resource: 'common.resource',
      resourceName: 'common.resource',
      course: 'common.course'
    };
    const translationKey = keyMap[column];
    if (translationKey) {
      return this.localization.translate(translationKey);
    }
    return this.labelForField(column);
  }

  getStudentCourseColumnLabel(column: string): string {
    if (column === 'name') {
      return this.localization.translate('common.course');
    }
    if (column === 'lessonsNumber') {
      return this.localization.translate('field.practiceLessonHours');
    }
    return this.getColumnLabel(column);
  }

  getCourseProgressLabel(row: Record<string, unknown>): string {
    return this.formatCourseProgress(row) || '-';
  }

  getCourseProgressPercent(row: Record<string, unknown>): number {
    const progress = this.resolveCourseProgress(row);
    if (progress.percent != null) {
      return Math.max(0, Math.min(100, progress.percent));
    }
    return progress.required > 0 ? Math.max(0, Math.min(100, (progress.completed / progress.required) * 100)) : 0;
  }

  getEntityStatusActions(): StatusAction[] {
    const key = this.entityKey();
    const current = this.row();
    if (!key || !current) {
      return [];
    }
    if ((key === 'students' || key === 'instructors' || key === 'courseInstances') && !this.canEditResources()) {
      return [];
    }
    if (key === 'payments' && !this.canAddPayment()) {
      return [];
    }
    if (key === 'lessons' && !this.canAddLesson()) {
      return [];
    }
    return this.resolveStatusActions(key, this.getEffectiveStatusForDetails(current, current.status));
  }

  hasMobileProfileActions(): boolean {
    if (this.canEditCurrentEntity()) {
      return true;
    }
    if (this.getEntityStatusActions().length > 0) {
      return true;
    }
    const key = this.entityKey();
    if (key === 'students') {
      return true;
    }
    if (key === 'instructors') {
      return true;
    }
    if (key === 'resources' && this.canEditResources()) {
      return true;
    }
    return false;
  }

  loginAsStudent(): void {
    if (this.entityKey() !== 'students' || !this.row() || !this.canEnterUserAppContext()) {
      return;
    }
    this.userContext.setStudentContext(this.row()!.id);
    this.router.navigate(['/student']);
  }

  loginAsInstructor(): void {
    if (this.entityKey() !== 'instructors' || !this.row() || !this.canEnterUserAppContext()) {
      return;
    }
    this.userContext.setInstructorContext(this.row()!.id);
    this.router.navigate(['/instructor']);
  }

  async sendInstructorInvite(): Promise<void> {
    if (this.entityKey() !== 'instructors' || !this.row() || this.invitingInstructor()) {
      return;
    }
    this.confirmImmediateAction(this.localization.translate('invitationSettings.manualInvite'), () => void this.sendInstructorInviteConfirmed());
  }

  private async sendInstructorInviteConfirmed(): Promise<void> {
    if (!this.row() || this.invitingInstructor()) return;
    this.invitingInstructor.set(true);
    try {
      await this.dataService.inviteInstructor(this.row()!.id);
      this.refreshCurrentDetails();
    } catch {
      // Error snackbar is handled in the data service.
    } finally {
      this.invitingInstructor.set(false);
    }
  }

  canInviteCurrentInstructor(): boolean {
    const row = this.row();
    return this.canEditResources() && this.entityKey() === 'instructors' && !!row && row['invited'] !== true && row['credentialsActive'] !== true;
  }

  applyEntityStatusAction(nextStatus: string): void {
    const key = this.entityKey();
    const current = this.row();
    if (!key || !current) {
      return;
    }
    if (key === 'lessons' && nextStatus === 'cancelled') {
      const dialogRef = this.dialog.open(CancelLessonDialogComponent, {
        width: '520px',
        maxWidth: 'calc(100vw - 32px)'
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          void this.runDetailMutation(this.dataService.setStatus('lessons', current.id, 'cancelled', result));
        }
      });
      return;
    }
    this.confirmImmediateAction(this.entityStatusActionLabel(nextStatus), () => {
      void this.runDetailMutation(this.dataService.setStatus(key, current.id, nextStatus));
    });
  }

  openEditInstructorRates(): void {
    if (this.entityKey() !== 'instructors' || !this.row()) {
      return;
    }

    const instructorRow = this.row()!;
    const titleKey = 'resourceDetails.editInstructorRates';
    const title = this.localization.translate(titleKey);
    const dialogRef = this.dialog.open(EditInstructorRatesDialogComponent, {
      width: '640px',
      maxWidth: '96vw',
      data: {
        title: title === titleKey ? 'Edit instructor rates' : title,
        rates: this.getInstructorRateRows(instructorRow)
      }
    });

    dialogRef.afterClosed().subscribe((result: EditInstructorRatesDialogResult | null | undefined) => {
      if (!result) {
        return;
      }
      void this.runDetailMutation(this.dataService.updateDynamic('instructors', instructorRow.id, {
        hourlyRateByCategory: result.hourlyRateByCategory,
        ...(result.rateChangeReason ? { rateChangeReason: result.rateChangeReason } : {})
      }));
    });
  }

  openEditCurrentResource(): void {
    if (this.entityKey() !== 'resources' || !this.row() || !this.canEditResources()) {
      return;
    }

    const current = this.row()!;
    const dialogRef = this.dialog.open(EditResourceDialogComponent, {
      width: '980px',
      maxWidth: '96vw',
      data: {
        resource: {
          id: current.id,
          name: `${current['name'] ?? ''}`,
          registration: `${current['registration'] ?? ''}`,
          category: this.asStringArray(current['category'] ?? current['categories'])[0] ?? '',
          type: `${current['type'] ?? ''}`,
          subType: `${current['subType'] ?? ''}`,
          nextServiceDate: (current['nextServiceDate'] as string | null | undefined) ?? null,
          issuanceExpiryDate: (current['issuanceExpiryDate'] as string | null | undefined) ?? null
        },
        categoryOptions: this.dataService.categories().map((item) => ({ value: item.name, label: item.name })),
        typeOptions: this.resourceTypeOptions(),
        subTypeOptions: this.resourceSubTypeOptions()
      }
    });

    dialogRef.afterClosed().subscribe((patch: Record<string, unknown> | null | undefined) => {
      if (!patch) {
        return;
      }
      const category = `${patch['category'] ?? current['category'] ?? ''}`.trim();
      void this.runDetailMutation(this.dataService.updateDynamic('resources', current.id, {
        ...patch,
        category,
        categories: category ? [category] : this.asStringArray(current['categories']),
        type: normalizeResourceType(patch['type'] ?? current['type']),
        subType: normalizeResourceSubType(patch['subType'] ?? current['subType'])
      }));
    });
  }

  canEditCurrentEntity(): boolean {
    const key = this.entityKey();
    const current = this.row();
    if (!key || !current || !this.canEditResources()) {
      return false;
    }
    if (key === 'courseInstances') {
      return this.canEditCurrentCourseInstance();
    }
    return key === 'students' || key === 'instructors' || key === 'courses' || key === 'payments' || key === 'lessons' || key === 'resources';
  }

  async openEditCurrentEntity(): Promise<void> {
    const config = this.entity();
    const current = this.row();
    if (!config || !current || !this.canEditCurrentEntity()) {
      return;
    }
    if (config.key === 'resources') {
      this.openEditCurrentResource();
      return;
    }
    if (config.key === 'courseInstances') {
      this.openEditCurrentCourseInstance();
      return;
    }

    if (config.key === 'lessons') {
      try {
        await this.dataService.loadLessonEditReferences(current);
      } catch {
        // Loading errors are reported by the data service.
        return;
      }
    }

    const dialogRef = this.dialog.open(CreateResourceDialogComponent, {
      data: {
        entity: config,
        mode: 'edit',
        initialValues: this.buildEditInitialValues(config.key, current),
        selectOptions: this.buildSelectOptions(config.key),
        statusOptions: config.statuses ?? []
      },
      width: config.key === 'instructors' ? '1180px' : '1120px',
      maxHeight: '95vh',
      maxWidth: '96vw'
    });

    dialogRef.afterClosed().subscribe((payload: Record<string, unknown> | null | undefined) => {
      if (!payload) {
        return;
      }
      void this.applyEntityEdit(config.key, current, payload).catch(() => undefined);
    });
  }

  canEditCurrentCourseInstance(): boolean {
    const current = this.row();
    if (this.entityKey() !== 'courseInstances' || !current || !this.canEditResources()) {
      return false;
    }
    return this.getEffectiveStatusForDetails(current, current.status) !== 'finished';
  }

  openEditCurrentCourseInstance(): void {
    const config = this.entity();
    const current = this.row();
    if (!config || config.key !== 'courseInstances' || !current || !this.canEditCurrentCourseInstance()) {
      return;
    }

    const dialogRef = this.dialog.open(CreateResourceDialogComponent, {
      data: {
        entity: config,
        mode: 'edit',
        initialValues: {
          ...current,
          theoreticalLessonsNumber: this.asNumber(current['theoreticalLessonsNumber'] ?? current['theoryHours']),
          practicalLessonsNumber: this.asNumber(current['practicalLessonsNumber'] ?? current['practiceHours'])
        },
        selectOptions: {
          courseId: this.dataService.courses().map((item) => ({
            value: item.id,
            label: `${item.name} (${(item.categories ?? []).join(', ')})`
          }))
        },
        courseTypes: Object.fromEntries(
          this.dataService.courses().map((course) => [String(course.id), course.courseType ?? 'STANDARD'])
        ),
        statusOptions: config.statuses ?? []
      },
      width: '1120px',
      maxHeight: '95vh',
      maxWidth: '96vw'
    });

    dialogRef.afterClosed().subscribe((payload: Record<string, unknown> | null | undefined) => {
      if (!payload) {
        return;
      }
      void this.runDetailMutation(this.dataService.updateDynamic('courseInstances', current.id, {
        name: `${payload['name'] ?? current['name'] ?? ''}`,
        courseId: payload['courseId'] ?? current['courseId'] ?? '',
        price: Number(payload['price'] ?? current['price'] ?? 0),
        billingModel: payload['billingModel'] ?? current['billingModel'] ?? 'FIXED_PRICE',
        studentHourlyPrice: payload['studentHourlyPrice'] ?? null,
        dateStarted: `${payload['dateStarted'] ?? current['dateStarted'] ?? ''}`,
        status: `${payload['status'] ?? current['status']}`
      }));
    });
  }

  private buildEditInitialValues(entityKey: EntityConfig['key'], row: DetailEntityRow): Record<string, unknown> {
    if (entityKey === 'students') {
      const contact = this.dataService.contacts().find((item) => this.sameId(item.id, row['contactId']));
      const address =
        this.dataService.addresses().find((item) => this.sameId(item.id, row['addressId'])) ??
        this.asEmbeddedAddress(row['address']);
      return {
        name: row['name'],
        surname: row['surname'],
        personalIdentificationNumber: '',
        personalIdentificationNumberMasked: row['personalIdentificationNumberMasked'],
        birthDate: row['birthDate'],
        email: contact?.email ?? '',
        mobile: contact?.mobile ?? '',
        streetName: address?.streetName ?? '',
        buildingNumber: address?.buildingNumber ?? '',
        postalCode: address?.postalCode ?? '',
        city: address?.city ?? '',
        country: address?.country ?? '',
        status: row['status']
      };
    }

    if (entityKey === 'instructors') {
      const contact = this.dataService.contacts().find((item) => this.sameId(item.id, row['contactId']));
      const address =
        this.dataService.addresses().find((item) => this.sameId(item.id, row['addressId'])) ??
        this.asEmbeddedAddress(row['address']);
      return {
        name: row['name'],
        surname: row['surname'],
        licenceNumber: row['licenceNumber'],
        licenceCategories: this.resolveLicenceCategoriesForEdit(row),
        hourlyRateByCategory: row['hourlyRateByCategory'],
        email: contact?.email ?? '',
        mobile: contact?.mobile ?? '',
        streetName: address?.streetName ?? '',
        buildingNumber: address?.buildingNumber ?? '',
        postalCode: address?.postalCode ?? '',
        city: address?.city ?? '',
        country: address?.country ?? '',
        status: row['status']
      };
    }

    if (entityKey === 'courses') {
      return {
        ...row,
        theoreticalLessonsNumber: this.asNonNegativeNumber(row['theoreticalLessonsNumber']),
        practicalLessonsNumber: this.asNonNegativeNumber(row['practicalLessonsNumber'])
      };
    }

    if (entityKey === 'lessons') {
      return {
        ...row,
        durationHours: Number((Number(row['durationMinutes'] ?? 0) / 60).toFixed(1))
      };
    }

    return { ...row };
  }

  private async applyEntityEdit(entityKey: EntityConfig['key'], row: DetailEntityRow, payload: Record<string, unknown>): Promise<void> {
    if (entityKey === 'students') {
      const studentPatch: Record<string, unknown> = {
        name: `${payload['name'] ?? ''}`,
        surname: `${payload['surname'] ?? ''}`,
        status: `${payload['status'] ?? row['status']}`
      };
      const birthDate = `${payload['birthDate'] ?? ''}`.trim();
      if (Object.prototype.hasOwnProperty.call(payload, 'personalIdentificationNumber')) {
        const replacementPesel = payload['personalIdentificationNumber'];
        studentPatch['personalIdentificationNumber'] = replacementPesel == null ? null : `${replacementPesel}`.trim();
      }
      if (birthDate) studentPatch['birthDate'] = birthDate;
      const updates: Array<Promise<void>> = [
        this.dataService.updateDynamic('students', row.id, studentPatch, { refreshEntity: false })
      ];

      if (row['contactId']) {
        updates.push(this.dataService.updateDynamic('contacts', row['contactId'] as string | number, {
          email: `${payload['email'] ?? ''}`,
          mobile: `${payload['mobile'] ?? ''}`
        }));
      }

      if (row['addressId']) {
        updates.push(this.dataService.updateDynamic('addresses', row['addressId'] as string | number, {
          streetName: `${payload['streetName'] ?? ''}`,
          buildingNumber: `${payload['buildingNumber'] ?? ''}`,
          postalCode: `${payload['postalCode'] ?? ''}`,
          city: `${payload['city'] ?? ''}`,
          country: `${payload['country'] ?? ''}`
        }));
      }

      await Promise.all(updates);
      this.refreshCurrentDetails();
      return;
    }

    if (entityKey === 'instructors') {
      const licenceCategories = this.asStringArray(payload['licenceCategories'] ?? payload['licenceCategoryIds']);
      const updates: Array<Promise<void>> = [
        this.dataService.updateDynamic('instructors', row.id, {
          name: `${payload['name'] ?? ''}`,
          surname: `${payload['surname'] ?? ''}`,
          licenceNumber: `${payload['licenceNumber'] ?? ''}`,
          licenceCategories,
          hourlyRateByCategory: this.buildInstructorHourlyRates(licenceCategories, payload['hourlyRateByCategory']),
          status: `${payload['status'] ?? row['status']}`
        }, { refreshEntity: false })
      ];

      if (row['contactId']) {
        updates.push(this.dataService.updateDynamic('contacts', row['contactId'] as string | number, {
          email: `${payload['email'] ?? ''}`,
          mobile: `${payload['mobile'] ?? ''}`
        }));
      }

      if (row['addressId']) {
        updates.push(this.dataService.updateDynamic('addresses', row['addressId'] as string | number, {
          streetName: `${payload['streetName'] ?? ''}`,
          buildingNumber: `${payload['buildingNumber'] ?? ''}`,
          postalCode: `${payload['postalCode'] ?? ''}`,
          city: `${payload['city'] ?? ''}`,
          country: `${payload['country'] ?? ''}`
        }));
      }

      await Promise.all(updates);
      this.refreshCurrentDetails();
      return;
    }

    if (entityKey === 'courses') {
      await this.dataService.updateDynamic('courses', row.id, {
        name: `${payload['name'] ?? ''}`,
        categories: this.asStringArray(payload['categories'] ?? row['categories']),
        theoryHours: this.asNonNegativeNumber(payload['theoryHours'], row['theoryHours']),
        practiceHours: this.asNonNegativeNumber(payload['practiceHours'], row['practiceHours']),
        status: `${payload['status'] ?? row['status']}`
      }, { refreshEntity: false });
      this.refreshCurrentDetails();
      return;
    }

    if (entityKey === 'payments') {
      await this.dataService.updateDynamic('payments', row.id, {
        studentId: payload['studentId'] ?? row['studentId'] ?? '',
        courseInstanceId: payload['courseInstanceId'] ?? row['courseInstanceId'] ?? '',
        amount: Number(payload['amount'] ?? row['amount'] ?? 0),
        paymentDate: `${payload['paymentDate'] ?? row['paymentDate'] ?? ''}`,
        paidTo: `${payload['paidTo'] ?? row['paidTo'] ?? ''}`,
        paymentType: `${payload['paymentType'] ?? row['paymentType'] ?? ''}`,
        additionalInformation: `${payload['additionalInformation'] ?? row['additionalInformation'] ?? ''}`,
        status: `${payload['status'] ?? row['status']}`
      }, { refreshEntity: false });
      this.refreshCurrentDetails();
      return;
    }

    if (entityKey === 'lessons') {
      await this.dataService.updateDynamic('lessons', row.id, {
        studentId: payload['studentId'] ?? row['studentId'] ?? '',
        instructorId: payload['instructorId'] ?? row['instructorId'] ?? '',
        courseInstanceId: payload['courseInstanceId'] ?? row['courseInstanceId'] ?? '',
        resourceId: payload['resourceId'] ?? row['resourceId'] ?? '',
        executionDate: `${payload['executionDate'] ?? row['executionDate'] ?? ''}`,
        lessonTime: `${payload['lessonTime'] ?? row['lessonTime'] ?? '08:00'}`,
        durationMinutes: Number(payload['durationHours'] ?? Number(row['durationMinutes'] ?? 60) / 60) * 60,
        status: `${payload['status'] ?? row['status']}`
      }, { refreshEntity: false });
      this.refreshCurrentDetails();
    }
  }

  private buildSelectOptions(
    entityKey: EntityConfig['key']
  ): Record<string, Array<{ value: string | number; label: string }>> {
    if (entityKey === 'courses') {
      return {
        categories: this.dataService.categories().map((item) => ({ value: item.name, label: item.name }))
      };
    }

    if (entityKey === 'instructors') {
      return {
        licenceCategories: this.dataService.categories().map((item) => ({ value: item.name, label: item.name }))
      };
    }

    if (entityKey === 'payments') {
      return {
        studentId: this.dataService.students().map((item) => ({ value: item.id, label: `${item.name} ${item.surname}` })),
        courseInstanceId: this.dataService.courseInstances().map((item) => ({ value: item.id, label: item.name })),
        paymentType: [
          { value: 'cash', label: this.localization.translate('common.paymentType.cash') },
          { value: 'card', label: this.localization.translate('common.paymentType.card') },
          { value: 'transfer', label: this.localization.translate('common.paymentType.transfer') }
        ]
      };
    }

    if (entityKey === 'lessons') {
      return {
        studentId: this.dataService.students().map((item) => ({ value: item.id, label: `${item.name} ${item.surname}` })),
        instructorId: this.dataService.instructors().map((item) => ({ value: item.id, label: `${item.name} ${item.surname}` })),
        courseInstanceId: this.dataService.courseInstances().map((item) => ({ value: item.id, label: item.name })),
        resourceId: this.dataService.resources().map((item) => ({
          value: item.id,
          label: item.registration ? `${item.name} (${item.registration})` : item.name
        }))
      };
    }

    return {};
  }

  getStudentPaymentStatusActions(row: Record<string, unknown>): StatusAction[] {
    const status = this.getEffectiveStatusForDetails(row, row['status']);
    if (status === 'pendingSettlement') {
      if (this.isInstructorCashPayment(row)) {
        return [{ nextStatus: 'settleCash', labelKey: 'payment.settleCash', icon: 'point_of_sale' }];
      }
      return [{ nextStatus: 'paid', labelKey: 'common.markPaid', icon: 'payments' }];
    }
    return [];
  }

  private isInstructorCashPayment(row: Record<string, unknown>): boolean {
    return `${row['collectedByInstructorId'] ?? ''}`.trim().length > 0;
  }

  readonly paymentSettlementsPending = signal<ReadonlySet<string>>(new Set());

  applyStudentPaymentStatusAction(row: Record<string, unknown>, nextStatus: string): void {
    const id = row['id'] as string | number | undefined;
    if (!id) {
      return;
    }
    if (nextStatus === 'settleCash') {
      const key = String(id);
      if (this.paymentSettlementsPending().has(key)) return;
      this.paymentSettlementsPending.update(ids => new Set([...ids, key]));
      const release = () => this.paymentSettlementsPending.update(ids => new Set([...ids].filter(item => item !== key)));
      const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
        width: '460px', maxWidth: 'calc(100vw - 24px)',
        data: { actionLabel: this.localization.translate('payment.settleCash') }
      });
      dialogRef.afterClosed().subscribe(confirmed => {
        if (!confirmed) { release(); return; }
        this.dataService.settlePaymentCash$(id).subscribe({
          next: payment => {
            Object.assign(row, payment);
            this.studentPaymentsReload.update((value) => value + 1);
            this.refreshCurrentDetails();
          },
          error: () => { release(); this.studentPaymentsReload.update((value) => value + 1); },
          complete: release
        });
      });
      return;
    }
    this.confirmImmediateAction(this.entityStatusActionLabel(nextStatus), () => {
      void this.runDetailMutation(this.dataService.setStatus('payments', id, nextStatus), () => {
        this.studentPaymentsReload.update((value) => value + 1);
      });
    });
  }

  getStudentCourseStatusActions(row: Record<string, unknown>): StatusAction[] {
    if (this.entityKey() !== 'students' || !this.row()) {
      return [];
    }
    const status = this.getEffectiveStatusForDetails(row, row['status']);
    if (!this.isOpenStudentCourseStatus(status)) {
      return [];
    }
    return [{ nextStatus: 'completed', labelKey: 'studentCourse.finish', icon: 'task_alt' }];
  }

  canMoveStudentCourse(row: Record<string, unknown>): boolean {
    if (this.entityKey() !== 'students' || !this.row() || !this.canAssignStudentRelations()) {
      return false;
    }
    return this.isOpenStudentCourseStatus(this.getEffectiveStatusForDetails(row, row['status']));
  }

  async openMoveStudentCourseDialog(row: Record<string, unknown>): Promise<void> {
    const student = this.row();
    const sourceCourseInstanceId = row['id'] as string | number | undefined;
    if (!this.canMoveStudentCourse(row) || !student || !sourceCourseInstanceId) {
      return;
    }

    const targetOptions = await this.fetchMoveStudentCourseTargetOptions(row);
    const dialogRef = this.dialog.open(MoveStudentCourseDialogComponent, {
      width: '520px',
      maxWidth: '96vw',
      disableClose: true,
      data: {
        studentId: student.id,
        sourceCourseInstanceId,
        sourceCourseName: `${row['name'] ?? ''}`,
        targetOptions
      }
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (!response) {
        return;
      }
      this.studentCoursesReload.update((value) => value + 1);
      this.refreshCurrentDetails();
    });
  }

  applyStudentCourseStatusAction(row: Record<string, unknown>, nextStatus: string): void {
    const student = this.row();
    const courseInstanceId = row['id'] as string | number | undefined;
    if (this.entityKey() !== 'students' || !student || !courseInstanceId || nextStatus !== 'completed') {
      return;
    }
    this.confirmImmediateAction(this.localization.translate('studentCourse.finish'), () => {
      void this.runDetailMutation(this.dataService.finishStudentCourseInstance(student.id, courseInstanceId), () => {
        this.studentCoursesReload.update((value) => value + 1);
      });
    });
  }

  openInstructorLessonsFilterDialog(): void {
    if (this.entityKey() !== 'instructors') {
      return;
    }
    const dialogRef = this.dialog.open(InstructorLessonsFilterDialogComponent, {
      width: '560px',
      maxWidth: '96vw',
      data: {
        dateFrom: this.instructorLessonDateFrom(),
        dateTo: this.instructorLessonDateTo(),
        studentId: this.instructorLessonStudentId(),
        students: this.instructorLessonStudentOptions()
      }
    });

    dialogRef.afterClosed().subscribe((result: InstructorLessonsFilterDialogResult | null | undefined) => {
      if (!result) {
        return;
      }
      this.instructorLessonDateFrom.set(result.dateFrom ?? '');
      this.instructorLessonDateTo.set(result.dateTo ?? '');
      this.instructorLessonStudentId.set(result.studentId ?? null);
    });
  }

  clearInstructorLessonsFilter(): void {
    this.instructorLessonDateFrom.set('');
    this.instructorLessonDateTo.set('');
    this.instructorLessonStudentId.set(null);
    this.instructorLessonsQuickFilter.set('all');
  }

  setInstructorLessonsQuickFilter(value: 'all' | 'completed' | 'planned'): void {
    this.instructorLessonsQuickFilter.set(value);
  }

  onInstructorLessonsPageChange(event: PageEvent): void {
    this.instructorLessonsPageIndex.set(event.pageIndex);
    this.instructorLessonsPageSize.set(event.pageSize);
  }

  setInstructorStudentsQuery(value: string): void {
    this.instructorStudentsQuery.set(value);
    this.instructorStudentsPageIndex.set(0);
  }

  setInstructorStudentsStatus(value: 'all' | 'active' | 'disabled'): void {
    this.instructorStudentsStatus.set(value);
    this.instructorStudentsPageIndex.set(0);
  }

  clearInstructorStudentsFilters(): void {
    this.instructorStudentsQuery.set('');
    this.instructorStudentsStatus.set('all');
    this.instructorStudentsPageIndex.set(0);
  }

  onInstructorStudentsPageChange(event: PageEvent): void {
    this.instructorStudentsPageIndex.set(event.pageIndex);
    this.instructorStudentsPageSize.set(event.pageSize);
  }

  private async fetchAvailableOptions(
    key: 'instructors' | 'courseInstances',
    filters: Record<string, unknown>,
    labelBuilder: (item: DetailEntityRow) => string,
    pageSize = 100
  ): Promise<Array<{ id: string | number; label: string }>> {
    try {
      const items = await this.dataService.fetchAllCollectionItems(key, filters, pageSize);
      return (items as unknown as DetailEntityRow[]).map((item) => ({ id: item.id, label: labelBuilder(item) }));
    } catch {
      return [];
    }
  }

  private async fetchMoveStudentCourseTargetOptions(sourceCourse: Record<string, unknown>): Promise<MoveStudentCourseOption[]> {
    const sourceCategories = this.resolveCourseCategories(sourceCourse);
    if (sourceCategories.length === 0) {
      return [];
    }

    try {
      const page = await firstValueFrom(
        this.dataService.fetchCollectionPage$('courseInstances', {
          page: 1,
          pageSize: 200,
          filters: { status: 'active', sort: 'dateStarted', order: 'desc' }
        })
      );
      return (page.items as unknown as DetailEntityRow[])
        .filter((course) => !this.sameId(course.id, sourceCourse['id']))
        .filter((course) => this.resolveCourseCategories(course).some((category) => sourceCategories.includes(category)))
        .map((course) => ({
          id: course.id,
          label: `${course['name'] ?? ''}`
        }));
    } catch {
      return [];
    }
  }

  async openAssignInstructorDialog(): Promise<void> {
    if (this.entityKey() !== 'students' || !this.row() || !this.canAssignStudentRelations()) {
      return;
    }

    const options = await this.fetchAvailableOptions('instructors', {
      status: 'ACTIVE',
      availableForStudentId: this.row()!.id,
      sort: 'surname',
      order: 'asc'
    }, (item) =>
      `${item['name'] ?? ''} ${item['surname'] ?? ''}`.trim(), 50
    );
    const dialogRef = this.dialog.open(AssignRelationDialogComponent, {
      width: '420px',
      data: {
        title: this.localization.translate('resourceDetails.assignInstructor'),
        label: this.localization.translate('resourceDetails.availableInstructors'),
        options,
        emptyMessage: this.localization.translate('resourceDetails.noAvailableInstructors'),
        actionLabel: this.localization.translate('resourceDetails.assignInstructor')
      }
    });

    dialogRef.afterClosed().subscribe((instructorId) => {
      if (!instructorId) {
        return;
      }
      void this.runDetailMutation(this.dataService.assignStudentToInstructor(this.row()!.id, instructorId, true));
    });
  }

  async openAssignCourseDialog(): Promise<void> {
    if (this.entityKey() !== 'students' || !this.row() || !this.canAssignStudentRelations()) {
      return;
    }

    const options = await this.fetchAvailableOptions('courseInstances', { availableForStudentId: this.row()!.id, sort: 'dateStarted', order: 'desc' }, (item) =>
      `${item['name'] ?? ''}`
    );
    const dialogRef = this.dialog.open(AssignRelationDialogComponent, {
      width: '420px',
      data: {
        title: this.localization.translate('resourceDetails.assignCourse'),
        label: this.localization.translate('resourceDetails.availableCourseInstances'),
        options,
        emptyMessage: this.localization.translate('resourceDetails.noAvailableCourses'),
        actionLabel: this.localization.translate('resourceDetails.assignCourse')
      }
    });

    dialogRef.afterClosed().subscribe((courseInstanceId) => {
      if (!courseInstanceId) {
        return;
      }
      void this.runDetailMutation(this.dataService.assignStudentToCourseInstance(this.row()!.id, courseInstanceId, true), () => {
        this.studentCoursesReload.update((value) => value + 1);
      });
    });
  }

  async openAssignCourseToInstructorDialog(): Promise<void> {
    if (this.entityKey() !== 'instructors' || !this.row() || !this.canAssignStudentRelations()) {
      return;
    }

    const options = await this.fetchAvailableOptions('courseInstances', { availableForInstructorId: this.row()!.id, sort: 'dateStarted', order: 'desc' }, (item) =>
      `${item['name'] ?? ''}`
    );
    const dialogRef = this.dialog.open(AssignRelationDialogComponent, {
      width: '420px',
      data: {
        title: this.localization.translate('resourceDetails.assignCourse'),
        label: this.localization.translate('resourceDetails.availableCourseInstances'),
        options,
        emptyMessage: this.localization.translate('resourceDetails.noAvailableCourses'),
        actionLabel: this.localization.translate('resourceDetails.assignCourse')
      }
    });

    dialogRef.afterClosed().subscribe((courseInstanceId) => {
      if (!courseInstanceId) {
        return;
      }
      void this.runDetailMutation(this.dataService.assignInstructorToCourseInstance(this.row()!.id, courseInstanceId, true));
    });
  }

  openAssignResourceToInstructorDialog(): void {
    if (this.entityKey() !== 'instructors' || !this.row() || !this.canAssignStudentRelations()) {
      return;
    }

    const options = this.availableResourceAssignmentsForInstructor();
    const dialogRef = this.dialog.open(AssignRelationDialogComponent, {
      width: '480px',
      data: {
        title: this.localization.translate('resourceDetails.assignResource'),
        label: this.localization.translate('entity.resources'),
        options: options.map((item) => ({ id: item.id, label: item.label })),
        emptyMessage: this.localization.translate('resourceDetails.noAvailableResourcesForInstructor'),
        actionLabel: this.localization.translate('resourceDetails.assignResource')
      }
    });

    dialogRef.afterClosed().subscribe((selectedOptionId) => {
      if (!selectedOptionId) {
        return;
      }

      const selected = options.find((item) => this.sameId(item.id, selectedOptionId));
      if (!selected) {
        return;
      }

      void this.runDetailMutation(this.dataService.assignResourceToInstructor(this.row()!.id, selected.resourceId, true));
    });
  }

  openInstructorAvailability(): void {
    if (this.entityKey() !== 'instructors' || !this.row()) return;
    this.dialog.open(InstructorAvailabilityDialogComponent, {
      width: '920px', maxWidth: '96vw', maxHeight: '94vh',
      data: { instructorId: this.row()!.id, instructorName: this.profileName() }
    });
  }

  openInstructorUnavailability(): void {
    if (this.entityKey() !== 'instructors' || !this.row()) return;
    this.dialog.open(InstructorUnavailabilityDialogComponent, {
      width: '720px', maxWidth: '96vw', maxHeight: '94vh',
      data: { instructorId: this.row()!.id, instructorName: this.profileName() }
    });
  }

  openInstructorCalendar(): void {
    if (this.entityKey() !== 'instructors' || !this.row()) return;
    this.dialog.open(InstructorCalendarDialogComponent, {
      width:'100vw',height:'100vh',maxWidth:'100vw',maxHeight:'100vh',panelClass:'fullscreen-calendar-dialog',
      data:{instructorId:this.row()!.id,instructorName:this.profileName(),instructorPortalMode:false}
    });
  }

  openResourceUnavailability(): void {
    if (this.entityKey() !== 'resources' || !this.row()) return;
    this.dialog.open(InstructorUnavailabilityDialogComponent, {
      width: '720px', maxWidth: '96vw', maxHeight: '94vh',
      data: { resourceId: this.row()!.id, resourceName: this.profileName() }
    });
  }

  openResourceCalendar(): void {
    if (this.entityKey() !== 'resources' || !this.row()) return;
    this.dialog.open(InstructorCalendarDialogComponent, {
      width:'100vw',height:'100vh',maxWidth:'100vw',maxHeight:'100vh',panelClass:'fullscreen-calendar-dialog',
      data:{resourceId:this.row()!.id,resourceName:this.profileName()}
    });
  }

  isDefaultInstructorResource(resourceId: string | number): boolean {
    return this.sameId(this.row()?.['defaultResourceId'], resourceId);
  }

  setDefaultInstructorResource(resourceId: string | number): void {
    if (!this.row() || this.isDefaultInstructorResource(resourceId)) return;
    this.confirmImmediateAction(this.localization.translate('resourceDetails.setDefaultResource'), () => {
      void this.runDetailMutation(this.dataService.setDefaultInstructorResource(this.row()!.id, resourceId));
    });
  }

  private confirmImmediateAction(actionLabel: string, onConfirm: () => void, description?: string): void {
    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '460px',
      maxWidth: 'calc(100vw - 24px)',
      data: { actionLabel, description }
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) onConfirm();
    });
  }

  private entityStatusActionLabel(nextStatus: string): string {
    const keyByStatus: Record<string, string> = {
      active: 'common.activate',
      disabled: 'common.disable',
      suspended: 'common.suspend',
      finished: 'common.finish',
      paid: 'common.markPaid'
    };
    return this.localization.translate(keyByStatus[nextStatus] ?? `status.${nextStatus}`);
  }

  unassignResourceFromInstructor(resourceId: string | number): void {
    const resource = this.instructorResources().find((item) => this.sameId(item.id, resourceId));
    if (resource) this.confirmUnlinkRelation('instructorResource', resource);
  }

  confirmUnlinkRelation(kind: UnlinkRelationKind, related: DetailEntityRow): void {
    const owner = this.row();
    if (!owner || (!this.canAssignStudentRelations() && !this.canEditResources())) return;
    const relationName = this.firstText(related['name'], related['displayName'], related['surname']) || `${related.id}`;
    const dialogRef = this.dialog.open(UnlinkRelationDialogComponent, {
      width: '460px',
      maxWidth: 'calc(100vw - 24px)',
      data: { relationName }
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      let mutation: Promise<void>;
      switch (kind) {
        case 'studentInstructor':
          mutation = this.dataService.assignStudentToInstructor(owner.id, related.id, false);
          break;
        case 'studentCourse':
          mutation = this.dataService.assignStudentToCourseInstance(owner.id, related.id, false);
          break;
        case 'instructorStudent':
          mutation = this.dataService.assignStudentToInstructor(related.id, owner.id, false);
          break;
        case 'instructorCourse':
          mutation = this.dataService.assignInstructorToCourseInstance(owner.id, related.id, false);
          break;
        case 'instructorResource':
          mutation = this.dataService.assignResourceToInstructor(owner.id, related.id, false);
          break;
        case 'resourceCourse':
          mutation = this.dataService.assignResourceToCourseInstance(owner.id, related.id, false);
          break;
      }
      void this.runDetailMutation(mutation, () => {
        this.studentCoursesReload.update((value) => value + 1);
      });
    });
  }

  async openAssignCourseToResourceDialog(): Promise<void> {
    if (this.entityKey() !== 'resources' || !this.row() || !this.canEditResources()) {
      return;
    }

    const options = await this.fetchAvailableOptions('courseInstances', { availableForResourceId: this.row()!.id, sort: 'dateStarted', order: 'desc' }, (item) =>
      `${item['name'] ?? ''} (${this.asStringArray(item['categories'] ?? item['category']).join(', ')})`
    );
    const dialogRef = this.dialog.open(AssignRelationDialogComponent, {
      width: '420px',
      data: {
        title: this.localization.translate('resourceDetails.assignCourse'),
        label: this.localization.translate('resourceDetails.availableCourseInstances'),
        options,
        emptyMessage: this.localization.translate('resourceDetails.noAvailableCourses'),
        actionLabel: this.localization.translate('resourceDetails.assignCourse')
      }
    });

    dialogRef.afterClosed().subscribe((courseInstanceId) => {
      if (!courseInstanceId) {
        return;
      }
      void this.runDetailMutation(this.dataService.assignResourceToCourseInstance(this.row()!.id, courseInstanceId, true));
    });
  }

  openAddLessonForStudent(): void {
    if (this.entityKey() !== 'students' || !this.row() || !this.canAddLesson()) {
      return;
    }

    const dialogRef = this.dialog.open(AddLessonDialogComponent, {
      width: '980px',
      maxWidth: '96vw',
      data: {
        students: this.dataService.students(),
        instructors: this.dataService.instructors(),
        courseInstances: this.dataService.courseInstances(),
        resources: this.dataService.resources(),
        fixedStudentId: this.row()!.id,
        allowFuturePlanning: this.userContext.canPlanFutureLessons()
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.['created']) {
        return;
      }
      this.refreshCurrentDetails();
    });
  }

  openAddPaymentForStudent(): void {
    if (this.entityKey() !== 'students' || !this.row() || !this.canAddPayment()) {
      return;
    }

    const dialogRef = this.dialog.open(AddPaymentDialogComponent, {
      width: '860px',
      maxWidth: '96vw',
      data: {
        students: this.dataService.students(),
        courseInstances: this.dataService.courseInstances(),
        fixedStudentId: this.row()!.id,
        addedByUserId: this.userContext.currentUser().id,
        addedByDisplay: this.userContext.currentUser().displayName
      }
    });

    dialogRef.afterClosed().subscribe((payload) => {
      if (!payload) {
        return;
      }
      void this.runDetailMutation(this.dataService.createDynamicFromBackend('payments', {
        studentId: payload['studentId'] ?? this.row()!.id,
        courseInstanceId: payload['courseInstanceId'] ?? '',
        amount: Number(payload['amount'] ?? 0),
        paymentDate: `${payload['paymentDate'] ?? ''}`,
        paidTo: `${payload['paidTo'] ?? ''}`,
        paymentType: `${payload['paymentType'] ?? ''}`,
        additionalInformation: `${payload['additionalInformation'] ?? ''}`
      }), () => {
        this.studentPaymentsReload.update((value) => value + 1);
      });
    });
  }

  private refreshCurrentDetails(): void {
    this.detailsRefresh.update((value) => value + 1);
  }

  private async runDetailMutation(mutation: Promise<unknown>, afterSuccess?: () => void): Promise<void> {
    try {
      await mutation;
      afterSuccess?.();
      this.refreshCurrentDetails();
    } catch {
      // The data service displays the mutation error.
    }
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asNumber(value: unknown): number {
    const parsed = typeof value === 'number' ? value : Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private optionalNumber(value: unknown): number | undefined {
    if (value == null || `${value}`.trim().length === 0) {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private getPaymentBalance(row: Record<string, unknown>): {
    totalDue?: number;
    totalPaid?: number;
    paidAmount?: number;
    balance?: number;
    remainingAmount?: number;
    averageHourlyRate?: number;
    usedFunds?: number;
    rideBalance?: number;
    totalRequiredPracticeHours?: number;
    completedPracticeHours?: number;
  } | null {
    const value = row['paymentBalance'];
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }
    const raw = value as Record<string, unknown>;
    return {
      totalDue: this.optionalNumber(raw['totalDue']),
      totalPaid: this.optionalNumber(raw['totalPaid']),
      paidAmount: this.optionalNumber(raw['paidAmount']),
      balance: this.optionalNumber(raw['balance']),
      remainingAmount: this.optionalNumber(raw['remainingAmount']),
      averageHourlyRate: this.optionalNumber(raw['averageHourlyRate']),
      usedFunds: this.optionalNumber(raw['usedFunds']),
      rideBalance: this.optionalNumber(raw['rideBalance']),
      totalRequiredPracticeHours: this.optionalNumber(raw['totalRequiredPracticeHours']),
      completedPracticeHours: this.optionalNumber(raw['completedPracticeHours'])
    };
  }

  private asNumberArray(value: unknown): Array<string | number> {
    if (!Array.isArray(value)) {
      const single = `${value ?? ''}`.trim();
      return single ? [value as string | number] : [];
    }
    return value.filter((item) => `${item ?? ''}`.trim().length > 0) as Array<string | number>;
  }

  private asStringArray(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.map((item) => `${item ?? ''}`.trim()).filter((item) => item.length > 0);
    }
    const single = `${value ?? ''}`.trim();
    return single ? [single] : [];
  }

  private asEmbeddedAddress(value: unknown):
    | {
        streetName?: string;
        buildingNumber?: string;
        postalCode?: string;
        city?: string;
        country?: string;
      }
    | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }
    const row = value as Record<string, unknown>;
    return {
      streetName: typeof row['streetName'] === 'string' ? row['streetName'] : undefined,
      buildingNumber: typeof row['buildingNumber'] === 'string' ? row['buildingNumber'] : undefined,
      postalCode: typeof row['postalCode'] === 'string' ? row['postalCode'] : undefined,
      city: typeof row['city'] === 'string' ? row['city'] : undefined,
      country: typeof row['country'] === 'string' ? row['country'] : undefined
    };
  }

  private asNonNegativeNumber(value: unknown, fallback: unknown = 0): number {
    const parsed = Number(value ?? fallback);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Math.max(0, parsed);
  }

  private buildInstructorHourlyRates(rawCategoryIds: unknown, rawExplicitRates?: unknown): Record<string, number> {
    const categoryNames = this.asStringArray(rawCategoryIds);
    const explicitRates =
      rawExplicitRates && typeof rawExplicitRates === 'object'
        ? (rawExplicitRates as Record<string, unknown>)
        : undefined;
    const rates: Record<string, number> = {};
    categoryNames.forEach((categoryName) => {
      const parsedExplicit = Number(explicitRates?.[categoryName]);
      rates[categoryName] = Number.isFinite(parsedExplicit)
        ? parsedExplicit
        : resolveDefaultRateForCategoryName(categoryName);
    });
    return rates;
  }

  private resolveLicenceCategoriesForEdit(row: Record<string, unknown>): string[] {
    const direct = this.asStringArray(row['licenceCategories']);
    if (direct.length > 0) {
      return direct;
    }
    return this.asStringArray(row['licenceCategoryIds'])
      .map((id) => this.dataService.categories().find((item) => this.sameId(item.id, id))?.name ?? id)
      .filter((item) => item.trim().length > 0);
  }

  private isActiveCourseInstance(row: { status?: unknown }): boolean {
    return `${row.status ?? ''}` === 'active';
  }

  private resolveFieldValue(key: string, value: unknown, row?: Record<string, unknown>): string {
    if (key === 'birthDate') {
      return this.formatStudentBirthDate(value);
    }
    if (key === 'accountStatus') {
      return this.getAccountStatusLabel(row);
    }

    if (key === 'progress') {
      return this.formatCourseProgress(row);
    }

    if (key === 'cancellationSummary') {
      return this.formatCancellationSummary(row ?? {});
    }

    if ((key === 'cancellationChargeAmount' || key === 'totalDue') && row && this.entityKey() === 'students' && this.isCourseStatusRow(row)) {
      const relation = this.findStudentCourseRelation(this.row() ?? {}, row);
      const relationValue = relation?.[key];
      if (relationValue != null) {
        return this.formatMoney(relationValue);
      }
      if (key === 'totalDue') {
        return this.formatMoney(row['price']);
      }
      return this.formatMoney(0);
    }

    if (key === 'lessonsNumber' && row && this.entityKey() === 'students' && this.isCourseStatusRow(row)) {
      return this.formatHours(this.coursePracticeHours(row));
    }

    if (key === 'hourlyRate' && value == null) {
      return this.localization.translate('settlement.missingHistoricalRate');
    }

    if (value == null) {
      if (key === 'addressId' && row && row['address']) {
        return this.formatEmbeddedAddress(row['address']);
      }
      return '';
    }

    if (key === 'status') {
      const status = this.getEffectiveStatusForDetails(row, value);
      if (this.isInstructorCashPayment(row ?? {}) && status === 'pendingSettlement') {
        return this.localization.translate('payment.cashPendingOfficeSettlement');
      }
      return this.isCourseStatusRow(row) ? this.translateCourseStatus(status) : this.translateStatus(status);
    }

    if (key === 'cancellationConsequence') {
      if (value === 'WITH_CONSEQUENCES') {
        return this.localization.translate('lessonCancel.badgeWithConsequences');
      }
      if (value === 'WITHOUT_CONSEQUENCES') {
        return this.localization.translate('lessonCancel.badgeWithoutConsequences');
      }
    }

    if (key === 'cancellationReasonType') {
      if (value === 'EMERGENCY') {
        return this.localization.translate('lessonCancel.emergencyBadge');
      }
      if (value === 'NORMAL') {
        return this.localization.translate('lessonCancel.reasonType.NORMAL');
      }
    }

    if (key === 'cancelledBy') {
      return this.translateCancelledBy(value);
    }

    if (key === 'address') {
      return this.formatEmbeddedAddress(value);
    }

    if (key === 'country') {
      return this.formatCountry(value);
    }

    if (key === 'type') {
      return this.translateResourceType(value);
    }

    if (key === 'subType') {
      return this.translateResourceSubType(value);
    }

    if (key === 'paymentType') {
      return this.translatePaymentType(value);
    }

    if (key === 'courseType') {
      return this.localization.translate(value === 'AD_HOC' ? 'courseType.adHoc' : 'courseType.standard');
    }

    if (key === 'billingModel') {
      return this.localization.translate(value === 'HOURLY' ? 'billingModel.hourly' : 'billingModel.fixedPrice');
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => this.resolveReferenceValue(key, item))
        .filter((item) => item.trim().length > 0)
        .join(', ');
    }

    const embeddedReference = this.embeddedReferenceValue(row ?? {}, key);
    if (embeddedReference) {
      return embeddedReference;
    }

    if (key.endsWith('Id')) {
      return this.resolveReferenceValue(key, value);
    }

    if (this.isMoneyKey(key)) {
      return this.formatMoney(value);
    }

    if (this.isDateTimeKey(key)) {
      return this.formatBrowserDateTime(value);
    }

    if (this.isDateKey(key)) {
      return formatDisplayDate(value);
    }

    if (key === 'lessonTime') {
      return formatDisplayTime(value);
    }

    if (key === 'durationMinutes') {
      return `${Number((Number(value) / 60).toFixed(1))} ${this.localization.translate('common.hoursShort')}`;
    }

    if (key === 'monthKey') {
      return this.formatMonthKey(`${value ?? ''}`);
    }

    if (key === 'totalHours' || key === 'payableCancelledDurationHours') {
      return `${Number(value ?? 0).toFixed(1)} ${this.localization.translate('common.hoursShort')}`;
    }

    if (key === 'hourlyRateByCategory' && typeof value === 'object') {
      return this.formatHourlyRateByCategory(value);
    }

    if ((key === 'previousHourlyRateByCategory' || key === 'newHourlyRateByCategory') && typeof value === 'object') {
      return this.formatHourlyRateByCategory(value);
    }

    return `${value}`;
  }

  private getEffectiveStatusForDetails(row: Record<string, unknown> | undefined, rawStatus: unknown): string {
    const status = `${rawStatus ?? ''}`;
    if (!row) {
      return status;
    }

    if (this.entityKey() === 'students' && 'courseId' in row && 'lessonsNumber' in row) {
      return this.resolveStudentCourseRelationStatus(row, status);
    }

    return status;
  }

  private formatCourseProgress(row: Record<string, unknown> | undefined): string {
    if (!row) {
      return '';
    }
    if (row['courseType'] === 'AD_HOC' || (row['course'] as Record<string, unknown> | undefined)?.['courseType'] === 'AD_HOC') {
      return '';
    }
    const apiLabel = `${row['progressLabel'] ?? ''}`.trim();
    if (apiLabel) {
      return apiLabel;
    }
    const { completed, required, percent } = this.resolveCourseProgress(row);
    if (completed <= 0 && required <= 0 && percent == null) {
      return '';
    }
    const hoursPart =
      completed > 0 || required > 0
        ? `${this.formatHours(completed)} / ${this.formatHours(required)}`
        : '';
    const percentPart = percent != null ? `${Number(percent.toFixed(1))}%` : '';
    return [hoursPart, percentPart ? `(${percentPart})` : ''].filter((item) => item.length > 0).join(' ');
  }

  private resolveCourseProgress(row: Record<string, unknown>): { completed: number; required: number; percent?: number } {
    const progress = row['progress'];
    const progressRow =
      progress && typeof progress === 'object' && !Array.isArray(progress)
        ? (progress as Record<string, unknown>)
        : this.findStudentCourseRelation(this.row() ?? {}, row)?.['progress'];
    const normalizedProgress =
      progressRow && typeof progressRow === 'object' && !Array.isArray(progressRow) ? (progressRow as Record<string, unknown>) : {};
    const completed = this.optionalNumber(normalizedProgress['completedPracticeHours']) ?? this.courseCompletedPracticeHours(row);
    const required = this.optionalNumber(normalizedProgress['requiredPracticeHours']) ?? this.coursePracticeHours(row);
    const percent =
      this.optionalNumber(normalizedProgress['percent'] ?? row['progressPercent']) ??
      (required > 0 ? Math.min(100, (completed / required) * 100) : undefined);
    return { completed, required, percent };
  }

  private aggregateCourseProgress(courses: CourseInstance[]): { completed?: number; required?: number } {
    let hasProgress = false;
    let completed = 0;
    let required = 0;
    for (const course of courses) {
      const row = course as unknown as Record<string, unknown>;
      const progress = row['progress'];
      const progressRow =
        progress && typeof progress === 'object' && !Array.isArray(progress)
          ? (progress as Record<string, unknown>)
          : this.findStudentCourseRelation(this.row() ?? {}, row)?.['progress'];
      if (!progressRow || typeof progressRow !== 'object' || Array.isArray(progressRow)) {
        continue;
      }
      const completedHours = this.optionalNumber((progressRow as Record<string, unknown>)['completedPracticeHours']);
      const requiredHours = this.optionalNumber((progressRow as Record<string, unknown>)['requiredPracticeHours']);
      if (completedHours != null || requiredHours != null) {
        hasProgress = true;
        completed += completedHours ?? 0;
        required += requiredHours ?? this.coursePracticeHours(row);
      }
    }
    return hasProgress ? { completed, required } : {};
  }

  private coursePracticeHours(row: Record<string, unknown>): number {
    return this.asNumber(row['practiceHours'] ?? row['practicalLessonsNumber']);
  }

  private courseCompletedPracticeHours(row: Record<string, unknown>): number {
    const courseInstanceId = row['id'];
    if (!courseInstanceId || this.entityKey() !== 'students' || !this.row()) {
      return 0;
    }
    return (
      this.dataService
        .lessons()
        .filter((lesson) => this.sameId(lesson.studentId, this.row()!.id) && this.sameId(lesson.courseInstanceId, courseInstanceId))
        .reduce((sum, lesson) => sum + this.asNumber(lesson.durationMinutes), 0) / 60
    );
  }

  private resolveStudentCourseRelationStatus(courseInstanceRow: Record<string, unknown>, fallbackStatus: string): string {
    const student = this.row();
    if (!student) {
      return fallbackStatus;
    }

    const courseInstanceId = courseInstanceRow['id'];
    if (!courseInstanceId) {
      return fallbackStatus;
    }

    const relationStatus = this.getStudentCourseRelationStatus(student, courseInstanceRow);
    if (relationStatus) {
      return relationStatus;
    }

    const scopedLessons = this.dataService.lessons().filter(
      (item) => this.sameId(item.studentId, student.id) && this.sameId(item.courseInstanceId, courseInstanceId)
    );
    if (scopedLessons.length === 0) {
      return 'new';
    }

    const completedMinutes = scopedLessons
      .filter((item) => item.status === 'completed')
      .reduce((sum, item) => sum + item.durationMinutes, 0);
    const completedHours = completedMinutes / 60;
    const plannedHours = this.asNumber(courseInstanceRow['lessonsNumber']);

    if (fallbackStatus === 'finished') {
      return plannedHours > 0 && completedHours >= plannedHours ? 'closed' : 'completed';
    }

    return 'inProgress';
  }

  private getStudentCourseRelationStatus(student: Record<string, unknown>, courseInstanceRow: Record<string, unknown>): string {
    const directStatus = courseInstanceRow['assignmentStatus'];
    if (directStatus) {
      return this.normalizeStatusToken(directStatus);
    }
    const relation = this.findStudentCourseRelation(student, courseInstanceRow);
    const rawStatus = relation?.['status'] ?? relation?.['state'];
    if (rawStatus) {
      const status = this.normalizeStatusToken(rawStatus);
      if (status === 'finished') {
        return 'completed';
      }
      return status;
    }
    if (relation?.['dateCompleted'] || relation?.['completedAt'] || relation?.['finishedAt']) {
      return 'completed';
    }
    return '';
  }

  private findStudentCourseRelation(
    student: Record<string, unknown>,
    courseInstanceRow: Record<string, unknown>
  ): Record<string, unknown> | null {
    const courseInstanceId = courseInstanceRow['id'];
    const candidates = [
      student['courseSummaries'],
      student['studentCourseInstances'],
      student['courseInstances'],
      courseInstanceRow['studentCourseInstances'],
      courseInstanceRow['studentCourseInstance']
    ];
    for (const source of candidates) {
      if (!Array.isArray(source)) {
        continue;
      }
      const relation = source.find((item) => {
        if (!item || typeof item !== 'object') {
          return false;
        }
        const row = item as Record<string, unknown>;
        const studentId = row['studentId'] ?? row['student'] ?? student['id'];
        const relationCourseId = row['courseInstanceId'] ?? row['courseId'] ?? row['courseInstance'] ?? row['id'];
        return this.sameId(studentId, student['id']) && this.sameId(relationCourseId, courseInstanceId);
      });
      if (relation && typeof relation === 'object') {
        return relation as Record<string, unknown>;
      }
    }
    return null;
  }

  private isOpenStudentCourseStatus(status: string): boolean {
    return !['completed', 'closed', 'finished', 'cancelled', 'disabled'].includes(status);
  }

  private normalizeStatusToken(value: unknown): string {
    const raw = `${value ?? ''}`.trim();
    if (!raw) {
      return '';
    }
    return raw
      .toLowerCase()
      .replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
  }

  private resolveStatusActions(entityKey: string, status: string): StatusAction[] {
    if (entityKey === 'students') {
      if (status === 'active') {
        return [{ nextStatus: 'disabled', labelKey: 'common.disable', icon: 'block' }];
      }
      if (status === 'disabled') {
        return [{ nextStatus: 'active', labelKey: 'common.activate', icon: 'check_circle' }];
      }
      return [];
    }

    if (entityKey === 'instructors') {
      if (status === 'active') {
        return [
          { nextStatus: 'disabled', labelKey: 'common.disable', icon: 'block' },
          { nextStatus: 'suspended', labelKey: 'common.suspend', icon: 'pause_circle' }
        ];
      }
      if (status === 'disabled' || status === 'suspended') {
        return [{ nextStatus: 'active', labelKey: 'common.activate', icon: 'check_circle' }];
      }
      return [];
    }

    if (entityKey === 'courseInstances') {
      if (status === 'active') {
        return [{ nextStatus: 'finished', labelKey: 'common.finish', icon: 'task_alt' }];
      }
      return [];
    }

    if (entityKey === 'payments') {
      if (status === 'pendingSettlement') {
        return [{ nextStatus: 'paid', labelKey: 'common.markPaid', icon: 'payments' }];
      }
      return [];
    }

    if (entityKey === 'lessons') {
      if (status === 'planned') {
        return [{ nextStatus: 'cancelled', labelKey: 'common.cancelLesson', icon: 'event_busy' }];
      }
      return [];
    }

    return [];
  }

  private getInstructorRateRows(instructorRow: Record<string, unknown>): Array<{ category: string; rate: number }> {
    const rawIds = Array.isArray(instructorRow['licenceCategoryIds']) ? instructorRow['licenceCategoryIds'] : [];
    const categoryIds = rawIds.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
    const existingRates =
      instructorRow['hourlyRateByCategory'] && typeof instructorRow['hourlyRateByCategory'] === 'object'
        ? (instructorRow['hourlyRateByCategory'] as Record<string, unknown>)
        : {};
    const categoryNames = [
      ...categoryIds
        .map((categoryId) => this.dataService.categories().find((item) => this.sameId(item.id, categoryId))?.name)
        .filter((name): name is string => Boolean(name)),
      ...this.asStringArray(instructorRow['licenceCategories']),
      ...Object.keys(existingRates)
    ];
    const uniqueCategoryNames = Array.from(new Set(categoryNames.map((item) => item.trim()).filter(Boolean)));

    return uniqueCategoryNames
      .sort((a, b) => a.localeCompare(b))
      .map((categoryName) => {
        const explicit = Number(existingRates[categoryName]);
        return {
          category: categoryName,
          rate: Number.isFinite(explicit) ? explicit : resolveDefaultRateForCategoryName(categoryName)
        };
      });
  }

  private formatHourlyRateByCategory(value: unknown): string {
    if (!value || typeof value !== 'object') {
      return '';
    }

    const rates = value as Record<string, unknown>;
    return Object.entries(rates)
      .map(([category, rate]) => {
        const parsed = Number(rate);
        if (!Number.isFinite(parsed)) {
          return '';
        }
        return `${category}: ${this.formatMoney(parsed)}/${this.localization.translate('common.hoursShort')}`;
      })
      .filter((item) => item.length > 0)
      .join(', ');
  }

  private translateStatus(status: string): string {
    const translated = this.localization.translate(`status.${status}`);
    if (translated !== `status.${status}`) {
      return translated;
    }
    const fallback = this.localization.translate(`common.${status}`);
    return fallback === `common.${status}` ? status : fallback;
  }

  private translateCourseStatus(status: string): string {
    const translated = this.localization.translate(`courseStatus.${status}`);
    return translated === `courseStatus.${status}` ? this.translateStatus(status) : translated;
  }

  private translatePaymentType(value: unknown): string {
    const token = `${value ?? ''}`.trim();
    const normalized = token.toLowerCase();
    const translated = this.localization.translate(`common.paymentType.${normalized}`);
    return translated === `common.paymentType.${normalized}` ? token : translated;
  }

  private isCourseStatusRow(row: Record<string, unknown> | undefined): boolean {
    if (!row) {
      return false;
    }
    return 'lessonsNumber' in row && ('courseId' in row || 'dateStarted' in row || 'price' in row);
  }

  private resolveReferenceValue(key: string, value: unknown): string {
    const id = value;
    if (id == null || `${id}`.trim().length === 0) {
      return `${value ?? ''}`;
    }

    if (key === 'studentId' || key === 'studentIds') {
      const student = this.dataService.students().find((item) => this.sameId(item.id, id));
      return student ? `${student.name} ${student.surname}` : '';
    }
    if (key === 'instructorId' || key === 'instructorIds' || key === 'collectedByInstructorId') {
      const instructor = this.dataService.instructors().find((item) => this.sameId(item.id, id));
      return instructor ? `${instructor.name} ${instructor.surname}` : '';
    }
    if (key === 'licenceCategoryIds') {
      return this.dataService.categories().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'organisationId') {
      return this.dataService.organisations().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'courseInstanceId' || key === 'courseInstanceIds') {
      return this.dataService.courseInstances().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'courseId') {
      return this.dataService.courses().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'categoryId' || key === 'categoryIds') {
      return this.dataService.categories().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'resourceId') {
      return this.dataService.resources().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'addressId') {
      const address = this.dataService.addresses().find((item) => this.sameId(item.id, id));
      if (address) {
        return `${address.streetName} ${address.buildingNumber}, ${address.postalCode} ${address.city}`;
      }
      return '';
    }
    if (key === 'contactId') {
      const contact = this.dataService.contacts().find((item) => this.sameId(item.id, id));
      return contact ? `${contact.email} | ${contact.mobile}` : '';
    }

    return `${value ?? ''}`;
  }

  private embeddedReferenceValue(row: Record<string, unknown>, key: string): string {
    if (key === 'studentId') {
      return this.lessonReferenceText(row, 'student');
    }
    if (key === 'instructorId') {
      return this.lessonReferenceText(row, 'instructor');
    }
    if (key === 'collectedByInstructorId') {
      return this.firstText(
        row['collectedByInstructorName'],
        row['collectedByInstructorDisplayName'],
        row['collectedByDisplay'],
        row['paidTo']
      );
    }
    if (key === 'resourceId') {
      return this.lessonReferenceText(row, 'resource');
    }
    if (key === 'courseInstanceId') {
      return this.lessonReferenceText(row, 'courseInstance');
    }
    return '';
  }

  private lessonReferenceText(row: Record<string, unknown>, prefix: 'student' | 'instructor' | 'resource' | 'courseInstance'): string {
    return this.firstText(
      row[`${prefix}Name`],
      row[`${prefix}DisplayName`],
      this.summaryText(row[prefix])
    );
  }

  private summaryText(value: unknown): string {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return '';
    }
    const row = value as Record<string, unknown>;
    return this.firstText(row['displayName'], row['name']);
  }

  private firstText(...values: unknown[]): string {
    for (const value of values) {
      const text = `${value ?? ''}`.trim();
      if (text) {
        return text;
      }
    }
    return '';
  }

  private toLabel(key: string): string {
    const cleaned = key.endsWith('Id') ? key.slice(0, -2) : key;
    return cleaned
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (char) => char.toUpperCase())
      .trim();
  }

  private getAccountStatusLabel(row?: Record<string, unknown>): string {
    if (row?.['credentialsActive'] === true) {
      return this.localization.translate('accountStatus.loggedIn');
    }
    return this.localization.translate('accountStatus.notLoggedIn');
  }

  private labelForField(key: string): string {
    if (this.entityKey() === 'resources' && (key === 'name' || key === 'category')) {
      return this.localization.translate(key === 'name' ? 'field.resourceName' : 'field.categories');
    }
    const translated = this.localization.translate(`field.${key}`);
    if (translated !== `field.${key}`) {
      return translated;
    }
    return this.toLabel(key);
  }

  private translateCancelledBy(value: unknown): string {
    const key = `lessonCancel.cancelledBy.${value ?? ''}`;
    const translated = this.localization.translate(key);
    return translated === key ? `${value ?? ''}` : translated;
  }

  formatMoney(value: unknown): string {
    return formatPln(value);
  }

  private formatHours(value: unknown): string {
    return `${Number(this.asNumber(value).toFixed(1))} ${this.localization.translate('common.hoursShort')}`;
  }

  private isMoneyKey(key: string): boolean {
    return (
      key === 'amount' ||
      key === 'price' ||
      key === 'studentHourlyPrice' ||
      key === 'hourlyRate' ||
      key === 'payoutAmount' ||
      key === 'settlementAmount' ||
      key === 'grossPayoutAmount' ||
      key === 'cashCollectedAmount' ||
      key === 'netPayoutAmount' ||
      key === 'cancellationChargeAmount' ||
      key === 'totalDue' ||
      key === 'studentCancellationChargeAmount' ||
      key === 'completedPayoutAmount' ||
      key === 'plannedPayoutAmount'
    );
  }

  private isDateKey(key: string): boolean {
    return ['executionDate', 'paymentDate', 'dateStarted', 'dateCompleted', 'nextServiceDate', 'issuanceExpiryDate', 'cashSettledAt', 'cancelledAt'].includes(key);
  }

  private formatStudentBirthDate(value: unknown): string {
    const raw = `${value ?? ''}`.trim();
    if (!raw) return this.localization.translate('common.notProvided');
    if (raw === '1900-01-01') return this.localization.translate('student.identity.legacyMissing');
    return formatDisplayDate(raw);
  }

  private isDateTimeKey(key: string): boolean {
    return key === 'createdAt' || key === 'updatedAt';
  }

  private formatBrowserDateTime(value: unknown): string {
    const raw = `${value ?? ''}`.trim();
    const date = new Date(raw);
    if (!raw || Number.isNaN(date.getTime())) {
      return raw;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return new Intl.DateTimeFormat(this.resolveLocale(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone,
      timeZoneName: 'short'
    }).format(date);
  }

  private formatCancellationSummary(row: Record<string, unknown>): string {
    const status = `${row['status'] ?? ''}`;
    if (status !== 'cancelled') {
      return '';
    }
    const parts = [
      row['cancellationConsequence'] === 'WITH_CONSEQUENCES'
        ? this.localization.translate('lessonCancel.badgeWithConsequences')
        : row['cancellationConsequence'] === 'WITHOUT_CONSEQUENCES'
          ? this.localization.translate('lessonCancel.badgeWithoutConsequences')
          : this.translateStatus(status)
    ];
    if (row['cancellationReasonType'] === 'EMERGENCY') {
      parts.push(this.localization.translate('lessonCancel.emergencyBadge'));
    }
    const charge = this.asNumber(row['studentCancellationChargeAmount']);
    if (charge > 0) {
      parts.push(this.formatMoney(charge));
    }
    const reason = `${row['cancellationReason'] ?? ''}`.trim();
    if (reason) {
      parts.push(reason);
    }
    return parts.join(' | ');
  }

  private resolveLocale(): string {
    const language = this.userContext.currentLanguage();
    if (language === 'pl') {
      return 'pl-PL';
    }
    if (language === 'ua') {
      return 'uk-UA';
    }
    return 'en-US';
  }

  private getCategoryName(categoryId: number): string {
    return this.dataService.categories().find((item) => this.sameId(item.id, categoryId))?.name ?? 'B';
  }

  private getPrimaryCourseInstanceCategoryName(
    courseInstance: { categoryId?: number; categoryIds?: number[] } | null | undefined
  ): string {
    if (!courseInstance) {
      return this.getCategoryName(0);
    }
    const categoryId =
      Number(
        courseInstance.categoryId ??
          (Array.isArray(courseInstance.categoryIds) && courseInstance.categoryIds.length > 0
            ? courseInstance.categoryIds[0]
            : 0)
      ) || 0;
    return this.getCategoryName(categoryId);
  }

  private resolveCourseCategories(course: Record<string, unknown>): string[] {
    const direct = this.asStringArray(course['categories'] ?? course['category'])
      .map((item) => item.toUpperCase())
      .filter((item) => item.length > 0);
    if (direct.length > 0) {
      return direct;
    }

    const categoryIds = this.asNumberArray(course['categoryIds'] ?? course['categoryId']);
    const fromCategoryIds = categoryIds
      .map((id) => this.dataService.categories().find((category) => this.sameId(category.id, id))?.name ?? '')
      .map((item) => item.toUpperCase())
      .filter((item) => item.length > 0);
    if (fromCategoryIds.length > 0) {
      return fromCategoryIds;
    }

    const linkedCourse = this.dataService.courses().find((item) => this.sameId(item.id, course['courseId']));
    if (!linkedCourse) {
      return [];
    }
    return this.asStringArray(linkedCourse.categories)
      .map((item) => item.toUpperCase())
      .filter((item) => item.length > 0);
  }

  private formatMonthKey(key: string): string {
    const [year, month] = key.split('-').map((item) => Number(item));
    if (!year || !month) {
      return key;
    }
    return new Date(year, month - 1, 1).toLocaleDateString(this.resolveLocale(), { month: 'long', year: 'numeric' });
  }

  private formatEmbeddedAddress(value: unknown): string {
    if (!value || typeof value !== 'object') {
      return '';
    }
    const row = value as Record<string, unknown>;
    const streetName = typeof row['streetName'] === 'string' ? row['streetName'] : '';
    const buildingNumber = typeof row['buildingNumber'] === 'string' ? row['buildingNumber'] : '';
    const postalCode = typeof row['postalCode'] === 'string' ? row['postalCode'] : '';
    const city = typeof row['city'] === 'string' ? row['city'] : '';
    const country = this.formatCountry(row['country']);
    const line1 = [streetName, buildingNumber].filter((item) => item.length > 0).join(' ');
    const line2 = [postalCode, city].filter((item) => item.length > 0).join(' ');
    return [line1, line2, country].filter((item) => item.length > 0).join(', ');
  }

  private formatCountry(value: unknown): string {
    const code = `${value ?? ''}`.trim();
    if (code === 'PL' || code.toLowerCase() === 'polska' || code.toLowerCase() === 'poland') {
      return this.localization.translate('country.PL');
    }
    return code;
  }

  private translateResourceType(value: unknown): string {
    const normalized = normalizeResourceType(value);
    return normalized ? this.localization.translate(`resource.type.${normalized}`) : `${value ?? ''}`;
  }

  private translateResourceSubType(value: unknown): string {
    const normalized = normalizeResourceSubType(value);
    return normalized ? this.localization.translate(`resource.subType.${normalized}`) : `${value ?? ''}`;
  }

  private resourceTypeOptions(): Array<{ value: string; label: string }> {
    return RESOURCE_TYPE_OPTIONS.map((item) => ({ value: item.value, label: this.localization.translate(item.labelKey) }));
  }

  private resourceSubTypeOptions(): Array<{ value: string; label: string }> {
    return RESOURCE_SUB_TYPE_OPTIONS.map((item) => ({ value: item.value, label: this.localization.translate(item.labelKey) }));
  }

  private getInstructorRateByCategory(
    ratesByCategory: Record<string, number> | undefined,
    categoryName: string
  ): number {
    const direct = ratesByCategory?.[categoryName];
    if (typeof direct === 'number' && Number.isFinite(direct)) {
      return direct;
    }
    return resolveDefaultRateForCategoryName(categoryName);
  }

  private isCompletedLesson(lesson: { status: string; executionDate: string }): boolean {
    return lesson.status === 'completed';
  }

  private isPlannedLesson(lesson: { status: string; executionDate: string }): boolean {
    return lesson.status === 'planned';
  }

  private getLinkedTargetForCell(
    row: Record<string, unknown>,
    key: string
  ): { entityKey: 'students' | 'instructors' | 'resources'; id: string | number } | null {
    if (key === 'studentId') {
      const id = row['studentId'];
      return id ? { entityKey: 'students', id: id as string | number } : null;
    }

    if (key === 'instructorId') {
      const id = row['instructorId'];
      return id ? { entityKey: 'instructors', id: id as string | number } : null;
    }

    if (key === 'name' || key === 'surname') {
      const rowId = row['id'];
      if (!rowId) {
        return null;
      }
      if ('personalIdentificationNumberMasked' in row || 'hasPersonalIdentificationNumber' in row || 'birthDate' in row) {
        return { entityKey: 'students', id: rowId as string | number };
      }
      if ('licenceNumber' in row) {
        return { entityKey: 'instructors', id: rowId as string | number };
      }
      if ('registration' in row) {
        return { entityKey: 'resources', id: rowId as string | number };
      }
    }

    return null;
  }

  private sameId(left: unknown, right: unknown): boolean {
    return `${left ?? ''}` === `${right ?? ''}`;
  }

  private hasId(values: Set<unknown>, id: unknown): boolean {
    return Array.from(values).some((value) => this.sameId(value, id));
  }
}
